<script setup>
/* eslint-disable no-use-before-define */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import DealProductsAPI from 'dashboard/api/pipelines/dealProducts';
import AddAttribute from 'dashboard/routes/dashboard/settings/attributes/AddAttribute.vue';
import AddLabel from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';
import DealModal from 'dashboard/components/pipelines/DealModal.vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
  currentChat: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const showCreateModal = ref(false);
const selectedDealForForm = ref(null);
const creating = ref(false);
const loading = ref(false);
const editingDealId = ref(null);

const selectedPipelineId = ref(null);
const selectedStageId = ref(null);
const title = ref('');
const description = ref('');
const startDate = ref('');
const closeDate = ref('');
const selectedContactId = ref(null);
const selectedConversationId = ref(null);
const selectedAgentIds = ref([]);
const selectedLabelTitles = ref([]);

const showProductsPopover = ref(false);
const productsView = ref('summary');
const productSearch = ref('');
const selectedProducts = ref([]);

const couponCode = ref('');
const selectedCoupon = ref(null);
const manualDiscount = ref('0');
const surcharge = ref('0');
const shipping = ref('0');

const showCreateProductForm = ref(false);
const newProduct = ref({
  name: '',
  sku: '',
  price: '',
});

const activeDealPopoverId = ref(null);
const activeDealActionsId = ref(null);
const dealPopoverStyle = ref({ top: '120px', left: '24px' });
const dealPopoverSaving = ref(false);
const dealProductsView = ref('summary');
const dealProductSearch = ref('');
const dealSelectedProducts = ref([]);
const showCreateDealProductForm = ref(false);
const dealNewProduct = ref({
  name: '',
  sku: '',
  price: '',
});
const dealCouponCode = ref('');
const dealSelectedCoupon = ref(null);
const dealManualDiscount = ref('0');
const dealSurcharge = ref('0');
const dealShipping = ref('0');
const showLoseDealModal = ref(false);
const loseDealLoading = ref(false);
const selectedDealForLoss = ref(null);
const selectedLossReasonId = ref(null);
const lossJustification = ref('');
const showDeleteConfirmationPopup = ref(false);
const selectedDealForDelete = ref(null);
const deleteDealLoading = ref(false);
const showDealCustomFieldsModal = ref(false);
const customFieldsSaving = ref(false);
const selectedDealForCustomFields = ref(null);
const selectedCustomAttributeKey = ref(null);
const dealCustomFieldsDraft = ref({});
const dealCustomFieldKeys = ref([]);
const activeDealCustomFieldsPopoverId = ref(null);
const dealCustomFieldsPopoverStyle = ref({ top: '120px', left: '24px' });
const dealCustomFieldsSearch = ref('');
const activeCustomFieldListKey = ref(null);
const customFieldListSearch = ref('');
const customFieldListPopoverStyle = ref({ top: '120px', left: '24px' });
const customFieldListPopoverWidth = ref(320);
const showAddDealAttributeModal = ref(false);
const pipelineInboxPermissions = ref({});
const activeDealPriorityPopoverId = ref(null);
const dealPriorityPopoverStyle = ref({ top: '120px', left: '24px' });
const prioritySaving = ref(false);
const activeDealLabelsPopoverId = ref(null);
const dealLabelsPopoverStyle = ref({ top: '120px', left: '24px' });
const dealLabelsSearch = ref('');
const labelsSaving = ref(false);
const showAddLabelModal = ref(false);
const stageUpdatingDealId = ref(null);
const pipelineDealsRefreshTimer = ref(null);
const activeAssigneePopoverDealId = ref(null);
const assigneePopoverStyle = ref({ top: '120px', left: '24px' });
const assigneeSelection = ref([]);
const assigneeSearch = ref('');
const assigneeSaving = ref(false);

const allDeals = computed(() => {
  const getter = store.getters['deals/getDealsByContext'];
  if (typeof getter !== 'function') {
    return store.getters['deals/getDeals'] || [];
  }
  return getter({
    accountId: accountId.value,
    params: { conversation_id: Number(props.conversationId) },
  });
});
const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
const products = computed(() => store.getters['products/getProducts'] || []);
const coupons = computed(() => store.getters['coupons/getCoupons'] || []);
const labels = computed(() => store.getters['labels/getLabels'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const lossReasons = computed(
  () => store.getters['dealLossReasons/getLossReasons'] || []
);
const localContacts = ref([]);
const allConversations = computed(
  () => store.getters.getAllConversations || []
);
const isEditingMode = computed(() => Boolean(editingDealId.value));
const deleteMessageValue = computed(() => {
  const deal = selectedDealForDelete.value;
  if (!deal) return '';
  return ` ${deal.title || `Negócio #${deal.id}`}`;
});
const selectedLossReason = computed(() => {
  return (
    lossReasons.value.find(
      item => Number(item.id) === Number(selectedLossReasonId.value)
    ) || null
  );
});
const getDealConversationRouteId = deal =>
  Number(deal?.conversation_display_id || deal?.conversation_id || 0);

const conversationDeals = computed(() => {
  const conversationId = Number(props.conversationId);
  const availablePipelineIds = new Set(
    pipelines.value.map(item => Number(item.id)).filter(id => !Number.isNaN(id))
  );
  const hasPipelineScope = availablePipelineIds.size > 0;

  return allDeals.value
    .filter(deal => {
      if (getDealConversationRouteId(deal) !== conversationId) return false;
      if (!hasPipelineScope) return true;
      return availablePipelineIds.has(Number(deal.pipeline_id));
    })
    .sort((a, b) => {
      const aDate = new Date(a.created_at || 0).getTime();
      const bDate = new Date(b.created_at || 0).getTime();
      return bDate - aDate;
    });
});

const currentConversationInboxId = computed(() => {
  const candidates = [
    props.inboxId,
    props.currentChat?.inbox_id,
    props.currentChat?.meta?.inbox_id,
    props.currentChat?.meta?.inbox?.id,
    props.currentChat?.meta?.conversation?.inbox_id,
  ];
  const match = candidates.find(value => value !== undefined && value !== null);
  const parsed = Number(match);
  return Number.isNaN(parsed) ? null : parsed;
});

const normalizeIdArray = list => {
  if (!Array.isArray(list)) return [];
  return list
    .map(item => {
      if (typeof item === 'object' && item !== null) {
        return Number(item.id);
      }
      return Number(item);
    })
    .filter(id => !Number.isNaN(id));
};

const getPipelineInboxIds = pipeline => {
  if (Array.isArray(pipeline?.inbox_ids)) {
    return normalizeIdArray(pipeline.inbox_ids);
  }

  if (Array.isArray(pipeline?.inboxes)) {
    return normalizeIdArray(pipeline.inboxes);
  }

  const pipelineId = Number(pipeline?.id);
  const cached = pipelineInboxPermissions.value[pipelineId];
  if (Array.isArray(cached)) return cached;

  return [];
};

const pipelineOptions = computed(() => {
  const inboxId = currentConversationInboxId.value;
  return pipelines.value.filter(pipeline => {
    const inboxIds = getPipelineInboxIds(pipeline);
    if (inboxId === null) return false;
    return inboxIds.includes(inboxId);
  });
});

const createDealBlockedState = computed(() => {
  if (pipelines.value.length === 0) return 'no_agent_pipeline_access';
  if (currentConversationInboxId.value === null)
    return 'missing_conversation_inbox';
  if (pipelineOptions.value.length === 0) return 'no_pipeline_for_inbox';
  return null;
});

const createDealBlockedTitle = computed(() => {
  if (createDealBlockedState.value === 'no_agent_pipeline_access') {
    return t(
      'DEALS.CONVERSATION.DEAL_MODAL.BLOCKED.NO_AGENT_PIPELINE_ACCESS_TITLE'
    );
  }
  if (createDealBlockedState.value === 'missing_conversation_inbox') {
    return t(
      'DEALS.CONVERSATION.DEAL_MODAL.BLOCKED.MISSING_CONVERSATION_INBOX_TITLE'
    );
  }
  if (createDealBlockedState.value === 'no_pipeline_for_inbox') {
    return t(
      'DEALS.CONVERSATION.DEAL_MODAL.BLOCKED.NO_PIPELINE_FOR_INBOX_TITLE'
    );
  }
  return '';
});

const createDealBlockedDescription = computed(() => {
  if (createDealBlockedState.value === 'no_agent_pipeline_access') {
    return t(
      'DEALS.CONVERSATION.DEAL_MODAL.BLOCKED.NO_AGENT_PIPELINE_ACCESS_DESC'
    );
  }
  if (createDealBlockedState.value === 'missing_conversation_inbox') {
    return t(
      'DEALS.CONVERSATION.DEAL_MODAL.BLOCKED.MISSING_CONVERSATION_INBOX_DESC'
    );
  }
  if (createDealBlockedState.value === 'no_pipeline_for_inbox') {
    return t(
      'DEALS.CONVERSATION.DEAL_MODAL.BLOCKED.NO_PIPELINE_FOR_INBOX_DESC'
    );
  }
  return '';
});

const selectedPipeline = computed(() => {
  return pipelineOptions.value.find(
    pipeline => Number(pipeline.id) === Number(selectedPipelineId.value)
  );
});

const stageOptions = computed(() => {
  const stages = selectedPipeline.value?.stages || [];
  return [...stages].sort((a, b) => (a.position || 0) - (b.position || 0));
});
const pipelineSelectOptions = computed(() => {
  return pipelineOptions.value.map(pipeline => ({
    label: pipeline.name,
    value: pipeline.id,
  }));
});
const stageSelectOptions = computed(() => {
  return stageOptions.value.map(stage => ({
    label: stage.name,
    value: stage.id,
  }));
});
const contactSelectOptions = computed(() =>
  localContacts.value.map(contact => ({
    label: contact.name || contact.email || `Contato #${contact.id}`,
    value: contact.id,
  }))
);
const allowedConversationStatuses = new Set([
  'open',
  'resolved',
  'pending',
  'snoozed',
]);
const conversationSelectOptions = computed(() => {
  const sourceConversations = selectedContactId.value
    ? store.getters['contactConversations/getContactConversation'](
        selectedContactId.value
      ) || []
    : allConversations.value;

  return sourceConversations
    .filter(item => {
      if (!allowedConversationStatuses.has(item.status)) return false;
      return true;
    })
    .map(item => ({
      value: item.id,
      label: `#${item.id} - ${item?.meta?.sender?.name || item?.meta?.sender?.identifier || 'Conversa'}`,
    }));
});
const assigneeSelectOptions = computed(() =>
  agents.value.map(agent => ({
    label: agent.name,
    value: agent.id,
  }))
);
const labelSelectOptions = computed(() =>
  labels.value.map(label => ({
    label: label.title,
    value: label.title,
  }))
);
const pipelineAccessKey = computed(() =>
  [...pipelines.value]
    .map(item => Number(item.id))
    .filter(id => !Number.isNaN(id))
    .sort((a, b) => a - b)
    .join(',')
);
const PRIORITY_OPTIONS = [
  {
    value: 'urgent',
    icon: 'i-lucide-chevrons-up',
    color: '#dc2626',
    label: 'Urgente',
  },
  {
    value: 'high',
    icon: 'i-lucide-chevron-up',
    color: '#eab308',
    label: 'Alta',
  },
  { value: 'medium', icon: 'i-lucide-equal', color: '#3b82f6', label: 'Média' },
  {
    value: 'low',
    icon: 'i-lucide-chevron-down',
    color: '#64748b',
    label: 'Baixa',
  },
  {
    value: 'none',
    icon: 'i-lucide-minus',
    color: '#64748b',
    label: 'Sem prioridade',
  },
];
const dealAttributeDefinitions = computed(() => {
  const getter = store.getters['attributes/getAttributesByModel'];
  if (typeof getter !== 'function') return [];
  return getter('deal_attribute') || [];
});
const dealAttributeDefinitionOptions = computed(() => {
  const used = new Set(dealCustomFieldKeys.value);
  return dealAttributeDefinitions.value
    .filter(item => !used.has(item.attribute_key))
    .map(item => ({
      label: item.attribute_display_name,
      value: item.attribute_key,
    }));
});

const activeProducts = computed(() => {
  return products.value.filter(product => product.active !== false);
});

const filteredCatalogProducts = computed(() => {
  const term = productSearch.value.trim().toLowerCase();
  const selectedIds = selectedProducts.value.map(item =>
    Number(item.product_id)
  );

  return activeProducts.value.filter(product => {
    if (selectedIds.includes(Number(product.id))) return false;
    if (!term) return true;
    return (
      product.name?.toLowerCase().includes(term) ||
      product.sku?.toLowerCase().includes(term)
    );
  });
});

const filteredDealCatalogProducts = computed(() => {
  const term = dealProductSearch.value.trim().toLowerCase();
  const selectedIds = dealSelectedProducts.value.map(item =>
    Number(item.product_id)
  );

  return activeProducts.value.filter(product => {
    if (selectedIds.includes(Number(product.id))) return false;
    if (!term) return true;
    return (
      product.name?.toLowerCase().includes(term) ||
      product.sku?.toLowerCase().includes(term)
    );
  });
});

const subtotalProducts = computed(() => {
  return selectedProducts.value.reduce((total, item) => {
    return total + item.quantity * Number(item.unit_price || 0);
  }, 0);
});

const parsedManualDiscount = computed(() => Number(manualDiscount.value || 0));
const parsedSurcharge = computed(() => Number(surcharge.value || 0));
const parsedShipping = computed(() => Number(shipping.value || 0));

const couponDiscount = computed(() => {
  if (!selectedCoupon.value) return 0;

  const coupon = selectedCoupon.value;
  if (coupon.discount_type === 'percentage') {
    return (subtotalProducts.value * Number(coupon.value || 0)) / 100;
  }
  return Number(coupon.value || 0);
});

const effectiveDiscount = computed(() => {
  if (parsedManualDiscount.value > 0) return parsedManualDiscount.value;
  return couponDiscount.value;
});

const finalTotal = computed(() => {
  const total =
    subtotalProducts.value -
    effectiveDiscount.value +
    parsedSurcharge.value +
    parsedShipping.value;
  return total > 0 ? total : 0;
});

const dealSubtotalProducts = computed(() => {
  return dealSelectedProducts.value.reduce((total, item) => {
    return total + item.quantity * Number(item.unit_price || 0);
  }, 0);
});

const dealParsedManualDiscount = computed(() =>
  Number(dealManualDiscount.value || 0)
);
const dealParsedSurcharge = computed(() => Number(dealSurcharge.value || 0));
const dealParsedShipping = computed(() => Number(dealShipping.value || 0));

const dealCouponDiscount = computed(() => {
  if (!dealSelectedCoupon.value) return 0;
  const coupon = dealSelectedCoupon.value;
  if (coupon.discount_type === 'percentage') {
    return (dealSubtotalProducts.value * Number(coupon.value || 0)) / 100;
  }
  return Number(coupon.value || 0);
});

const dealEffectiveDiscount = computed(() => {
  if (dealParsedManualDiscount.value > 0) return dealParsedManualDiscount.value;
  return dealCouponDiscount.value;
});

const dealFinalTotal = computed(() => {
  const total =
    dealSubtotalProducts.value -
    dealEffectiveDiscount.value +
    dealParsedSurcharge.value +
    dealParsedShipping.value;
  return total > 0 ? total : 0;
});

const fetchData = async () => {
  loading.value = true;
  try {
    await Promise.all([
      store.dispatch('pipelines/get', { accountId: accountId.value }),
      store.dispatch('agents/get'),
      store.dispatch('deals/get', {
        accountId: accountId.value,
        params: { conversation_id: props.conversationId },
      }),
      store.dispatch('products/get', { accountId: accountId.value }),
      store.dispatch('coupons/get', { accountId: accountId.value }),
      store.dispatch('dealLossReasons/get', { accountId: accountId.value }),
      store.dispatch('attributes/get'),
      ContactAPI.get(1)
        .then(({ data }) => {
          localContacts.value = data?.payload || [];
        })
        .catch(() => null),
      store.dispatch('labels/get').catch(() => null),
    ]);
  } catch (error) {
    useAlert('Erro ao carregar dados de negócios');
  } finally {
    loading.value = false;
  }
};

const closeFloatingPanels = () => {
  activeDealActionsId.value = null;
  activeDealPopoverId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
  activeDealPriorityPopoverId.value = null;
  activeDealLabelsPopoverId.value = null;
  activeAssigneePopoverDealId.value = null;
  activeCustomFieldListKey.value = null;
  showProductsPopover.value = false;
};

const handleDocumentClick = event => {
  const target = event.target;

  if (
    activeDealActionsId.value &&
    !target.closest('[data-deal-actions-menu]') &&
    !target.closest('[data-deal-actions-trigger]')
  ) {
    activeDealActionsId.value = null;
  }

  if (
    activeDealPopoverId.value &&
    !target.closest('[data-deal-products-popover]') &&
    !target.closest('[data-deal-products-trigger]')
  ) {
    activeDealPopoverId.value = null;
  }

  if (
    activeDealCustomFieldsPopoverId.value &&
    !target.closest('[data-deal-custom-fields-popover]') &&
    !target.closest('[data-deal-custom-fields-trigger]')
  ) {
    activeDealCustomFieldsPopoverId.value = null;
  }

  if (
    activeDealPriorityPopoverId.value &&
    !target.closest('[data-deal-priority-menu]') &&
    !target.closest('[data-deal-priority-trigger]')
  ) {
    activeDealPriorityPopoverId.value = null;
  }

  if (
    activeDealLabelsPopoverId.value &&
    !target.closest('[data-deal-labels-popover]') &&
    !target.closest('[data-deal-labels-trigger]')
  ) {
    activeDealLabelsPopoverId.value = null;
  }

  if (
    activeAssigneePopoverDealId.value &&
    !target.closest('[data-deal-assignee-popover]') &&
    !target.closest('[data-deal-assignee-trigger]')
  ) {
    activeAssigneePopoverDealId.value = null;
  }

  if (
    activeCustomFieldListKey.value &&
    !target.closest('[data-custom-field-list-popover]') &&
    !target.closest('[data-custom-field-list-trigger]')
  ) {
    activeCustomFieldListKey.value = null;
  }

  if (
    showProductsPopover.value &&
    !target.closest('[data-create-products-popover]') &&
    !target.closest('[data-create-products-trigger]')
  ) {
    showProductsPopover.value = false;
  }
};

const handleEscapeKey = event => {
  if (event.key !== 'Escape') return;
  closeFloatingPanels();
};

onMounted(() => {
  fetchData();
  document.addEventListener('mousedown', handleDocumentClick);
  document.addEventListener('keydown', handleEscapeKey);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleDocumentClick);
  document.removeEventListener('keydown', handleEscapeKey);
  if (pipelineDealsRefreshTimer.value) {
    clearTimeout(pipelineDealsRefreshTimer.value);
    pipelineDealsRefreshTimer.value = null;
  }
});

const scheduleDealsReloadAfterPipelinePermissionChange = () => {
  if (pipelineDealsRefreshTimer.value) {
    clearTimeout(pipelineDealsRefreshTimer.value);
  }
  pipelineDealsRefreshTimer.value = setTimeout(async () => {
    try {
      await loadPipelineInboxPermissions();
      await store.dispatch('deals/get', {
        accountId: accountId.value,
        params: { conversation_id: props.conversationId },
      });
    } catch (error) {
      // noop
    } finally {
      pipelineDealsRefreshTimer.value = null;
    }
  }, 350);
};

watch(pipelineAccessKey, (newValue, oldValue) => {
  if (!oldValue || newValue === oldValue) return;
  scheduleDealsReloadAfterPipelinePermissionChange();
});

watch(selectedPipelineId, pipelineId => {
  if (!pipelineId) {
    selectedStageId.value = null;
    return;
  }
  const firstStage = stageOptions.value[0];
  selectedStageId.value = firstStage?.id || null;
});
watch(selectedContactId, contactIdValue => {
  if (!contactIdValue) return;
  store.dispatch('contactConversations/get', contactIdValue).catch(() => null);
});

watch(
  () => props.currentChat?.meta?.sender?.id,
  newContactId => {
    const parsed = Number(newContactId || 0);
    if (!parsed || editingDealId.value) return;
    selectedContactId.value = parsed;
  }
);

watch(
  () => props.conversationId,
  async (newConversationId, oldConversationId) => {
    const nextId = Number(newConversationId || 0);
    const prevId = Number(oldConversationId || 0);
    if (!nextId || nextId === prevId) return;

    editingDealId.value = null;
    selectedDealForForm.value = null;
    selectedConversationId.value = nextId;

    if (!showCreateModal.value) {
      const fallbackContactId = Number(
        props.currentChat?.meta?.sender?.id || 0
      );
      selectedContactId.value = fallbackContactId || null;
    }

    closeFloatingPanels();

    await Promise.allSettled([
      loadPipelineInboxPermissions(),
      store.dispatch('deals/get', {
        accountId: accountId.value,
        params: { conversation_id: newConversationId },
      }),
    ]);
  }
);

const formatCurrency = value => {
  const amountValue = Number(value || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amountValue);
};

const toSafeNumber = (value, min = 0) => {
  const parsed = Number(value);
  const normalized = Number.isFinite(parsed) ? parsed : 0;
  return Math.max(min, normalized);
};

const getDealDisplayAmount = deal => {
  const amount = Number(deal?.amount);
  if (Number.isFinite(amount) && amount > 0) return amount;

  const calculated = Number(deal?.custom_attributes?.calculated_total);
  if (Number.isFinite(calculated) && calculated > 0) return calculated;

  const fallbackFromProducts = (deal?.products || []).reduce((sum, item) => {
    const quantity = Number(item?.quantity || 0);
    const unitPrice = Number(item?.unit_price || 0);
    if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return sum;
    return sum + quantity * unitPrice;
  }, 0);

  return fallbackFromProducts > 0 ? fallbackFromProducts : 0;
};

const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

const formatDealCreatedAt = value => {
  if (!value) return '-';
  const createdAt = new Date(value);
  if (Number.isNaN(createdAt.getTime())) return '-';

  const diffMs = Date.now() - createdAt.getTime();
  if (diffMs >= 0 && diffMs < 24 * 60 * 60 * 1000) {
    if (diffMs < 60 * 60 * 1000) {
      return `${Math.max(1, Math.floor(diffMs / (60 * 1000)))}min`;
    }
    return `${Math.max(1, Math.floor(diffMs / (60 * 60 * 1000)))}h`;
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
  }).format(createdAt);
};

const getDealDisplayTitle = deal => {
  const normalizedTitle = (deal.title || '').trim();
  const titleLabel =
    normalizedTitle || t('DEALS.CONVERSATION.FALLBACK_DEAL_TITLE');
  return `${titleLabel} #${deal.id}`;
};

async function loadPipelineInboxPermissions() {
  const pipelineList = pipelines.value || [];
  if (!pipelineList.length) return;

  const responses = await Promise.allSettled(
    pipelineList.map(async pipeline => {
      const response = await store.dispatch('pipelineInboxes/get', {
        accountId: accountId.value,
        pipelineId: pipeline.id,
      });
      return [Number(pipeline.id), normalizeIdArray(response?.data?.payload)];
    })
  );

  const next = { ...pipelineInboxPermissions.value };
  responses.forEach(result => {
    if (result.status !== 'fulfilled') return;
    const [pipelineId, inboxIds] = result.value;
    next[pipelineId] = inboxIds;
  });
  pipelineInboxPermissions.value = next;
}

const getProductSummary = deal => {
  const productItems = deal.products || [];
  if (!productItems.length) return t('DEALS.CONVERSATION.PRODUCTS.EMPTY');
  const firstName =
    productItems[0].name || t('DEALS.CONVERSATION.PRODUCTS.DEFAULT_NAME');
  if (productItems.length === 1) return firstName;
  return `${firstName} (+${productItems.length - 1})`;
};

const getPipelineName = deal => {
  const pipeline = pipelines.value.find(
    item => Number(item.id) === Number(deal.pipeline_id)
  );
  return pipeline?.name || '-';
};

const getPipelineStages = deal => {
  const pipeline = pipelines.value.find(
    item => Number(item.id) === Number(deal.pipeline_id)
  );
  const stages = pipeline?.stages || [];
  return [...stages].sort((a, b) => (a.position || 0) - (b.position || 0));
};

const getStageProgressStyle = (stage, index, deal) => {
  const stages = getPipelineStages(deal);
  const currentIndex = stages.findIndex(
    item => Number(item.id) === Number(deal.pipeline_stage_id)
  );
  if (currentIndex === -1 || index > currentIndex) {
    return { backgroundColor: '#E5E7EB' };
  }
  return { backgroundColor: stage.color || '#6B7280' };
};

const getStageTooltip = (stage, deal) => {
  const isCurrent = Number(stage.id) === Number(deal.pipeline_stage_id);
  return isCurrent ? `${stage.name} (atual)` : stage.name;
};

const setDealStage = async (deal, stageId) => {
  if (!deal?.id) return;
  if (Number(deal.pipeline_stage_id) === Number(stageId)) return;
  stageUpdatingDealId.value = deal.id;
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: deal.id,
      deal: { pipeline_stage_id: stageId },
    });
    await refreshDealById(deal.id);
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('DEALS.CONVERSATION.DEAL_MODAL.ERROR_UPDATE')
    );
  } finally {
    stageUpdatingDealId.value = null;
  }
};

const resetPricingState = () => {
  selectedProducts.value = [];
  showProductsPopover.value = false;
  productsView.value = 'summary';
  productSearch.value = '';
  couponCode.value = '';
  selectedCoupon.value = null;
  manualDiscount.value = '0';
  surcharge.value = '0';
  shipping.value = '0';
  showCreateProductForm.value = false;
  newProduct.value = { name: '', sku: '', price: '' };
};

const openCreateModal = async () => {
  await loadPipelineInboxPermissions();
  const firstPipeline = pipelineOptions.value[0];
  selectedPipelineId.value = firstPipeline?.id || null;
  selectedStageId.value = firstPipeline?.stages?.[0]?.id || null;
  title.value = '';
  description.value = '';
  startDate.value = '';
  closeDate.value = '';
  selectedContactId.value = Number(props.currentChat?.meta?.sender?.id) || null;
  selectedConversationId.value = Number(props.conversationId) || null;
  selectedAgentIds.value = [];
  selectedLabelTitles.value = [];
  selectedDealForForm.value = null;
  resetPricingState();
  showCreateModal.value = true;
};

const closeCreateModal = () => {
  showCreateModal.value = false;
  editingDealId.value = null;
  selectedDealForForm.value = null;
};
const deleteEditingDeal = () => {
  if (!editingDealId.value) return;
  const deal = { id: editingDealId.value };
  openDeleteDealPopup(deal);
};

const RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS = new Set([
  'subtotal_products',
  'coupon_code',
  'coupon_discount',
  'manual_discount',
  'surcharge',
  'shipping',
  'calculated_total',
]);

const isCustomFieldValuePresent = value => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return true;
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const formatCustomFieldValue = value => {
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const getDealCustomFieldsEntries = deal => {
  const attrs = deal.custom_attributes || {};
  const definitionsByKey = new Map(
    dealAttributeDefinitions.value.map(item => [item.attribute_key, item])
  );

  return Object.entries(attrs)
    .filter(([key, value]) => {
      if (RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS.has(key)) return false;
      if (!definitionsByKey.has(key)) return false;
      return isCustomFieldValuePresent(value);
    })
    .map(([key, value]) => {
      const definition = definitionsByKey.get(key);
      return {
        key,
        label: definition.attribute_display_name,
        type: definition.attribute_display_type,
        value,
        formattedValue: formatCustomFieldValue(value),
      };
    });
};

const getDealCustomFieldsCount = deal =>
  getDealCustomFieldsEntries(deal).length;

const getDealCustomFieldsForPopover = deal => {
  const term = dealCustomFieldsSearch.value.trim().toLowerCase();
  const entries = getDealCustomFieldsEntries(deal);
  if (!term) return entries;
  return entries.filter(item => {
    return (
      item.label?.toLowerCase().includes(term) ||
      item.formattedValue?.toLowerCase().includes(term)
    );
  });
};

const setDealCustomFieldsPopoverPosition = event => {
  dealCustomFieldsPopoverStyle.value = setSmallPopoverPosition(event, {
    width: 360,
    preferredHeight: 420,
    minHeight: 220,
    openThreshold: 280,
    align: 'start',
  });
};

const toggleDealCustomFieldsPopover = (deal, event) => {
  if (activeDealCustomFieldsPopoverId.value === deal.id) {
    activeDealCustomFieldsPopoverId.value = null;
    return;
  }
  activeDealActionsId.value = null;
  activeDealPopoverId.value = null;
  activeDealPriorityPopoverId.value = null;
  activeDealLabelsPopoverId.value = null;
  setDealCustomFieldsPopoverPosition(event);
  dealCustomFieldsSearch.value = '';
  activeDealCustomFieldsPopoverId.value = deal.id;
};

const closeDealCustomFieldsPopover = () => {
  activeDealCustomFieldsPopoverId.value = null;
};

const getDealPriorityValue = deal => {
  const priority = deal?.priority || deal?.custom_attributes?.priority;
  const exists = PRIORITY_OPTIONS.some(item => item.value === priority);
  return exists ? priority : 'none';
};

const getDealPriorityMeta = deal => {
  const value = getDealPriorityValue(deal);
  return (
    PRIORITY_OPTIONS.find(item => item.value === value) || PRIORITY_OPTIONS[4]
  );
};

const getDealAssignees = deal => {
  const customIds = Array.isArray(
    deal?.custom_attributes?.responsible_agent_ids
  )
    ? deal.custom_attributes.responsible_agent_ids.map(id => Number(id))
    : [];
  const nativeId = Number(deal?.assignee?.id || deal?.assignee_id);
  const ids = [
    ...new Set([...customIds, nativeId].filter(id => !Number.isNaN(id))),
  ];
  if (!ids.length && deal?.assignee) return [deal.assignee];
  return agents.value.filter(agent => ids.includes(Number(agent.id)));
};

const filteredAssigneeOptions = computed(() => {
  const term = assigneeSearch.value.trim().toLowerCase();
  if (!term) return agents.value;
  return agents.value.filter(agent => agent.name?.toLowerCase().includes(term));
});

const isAssigneeSelected = agentId =>
  assigneeSelection.value.some(item => Number(item.id) === Number(agentId));

const toggleAssigneeSelection = agent => {
  if (isAssigneeSelected(agent.id)) {
    assigneeSelection.value = assigneeSelection.value.filter(
      item => Number(item.id) !== Number(agent.id)
    );
    return;
  }
  assigneeSelection.value = [...assigneeSelection.value, agent];
};

const setAssigneePopoverPosition = event => {
  assigneePopoverStyle.value = setSmallPopoverPosition(event, {
    width: 320,
    preferredHeight: 360,
    minHeight: 220,
    openThreshold: 260,
    align: 'start',
  });
};

const toggleAssigneePopover = (deal, event) => {
  if (activeAssigneePopoverDealId.value === deal.id) {
    activeAssigneePopoverDealId.value = null;
    return;
  }
  activeDealActionsId.value = null;
  activeDealPopoverId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
  activeDealPriorityPopoverId.value = null;
  activeDealLabelsPopoverId.value = null;
  setAssigneePopoverPosition(event);
  const assignees = getDealAssignees(deal);
  assigneeSelection.value = [...assignees];
  assigneeSearch.value = '';
  activeAssigneePopoverDealId.value = deal.id;
};

const saveDealAssignees = async deal => {
  assigneeSaving.value = true;
  try {
    const selectedIds = assigneeSelection.value.map(agent => Number(agent.id));
    const primaryAssigneeId = selectedIds[0] || null;
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: deal.id,
      deal: {
        assignee_id: primaryAssigneeId,
        custom_attributes: {
          ...(deal.custom_attributes || {}),
          responsible_agent_ids: selectedIds,
        },
      },
    });
    await refreshDealById(deal.id);
    activeAssigneePopoverDealId.value = null;
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || 'Erro ao atualizar responsaveis'
    );
  } finally {
    assigneeSaving.value = false;
  }
};

const toggleDealPriorityPopover = (deal, event) => {
  if (activeDealPriorityPopoverId.value === deal.id) {
    activeDealPriorityPopoverId.value = null;
    return;
  }
  activeDealActionsId.value = null;
  activeDealPopoverId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
  activeDealLabelsPopoverId.value = null;
  activeAssigneePopoverDealId.value = null;
  dealPriorityPopoverStyle.value = setSmallPopoverPosition(event, {
    width: 230,
    preferredHeight: 260,
  });
  activeDealPriorityPopoverId.value = deal.id;
};

const setDealPriority = async (deal, priority) => {
  prioritySaving.value = true;
  try {
    const nextPriority = priority === 'none' ? null : priority;
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: deal.id,
      deal: {
        priority: nextPriority,
      },
    });
    await refreshDealById(deal.id);
    activeDealPriorityPopoverId.value = null;
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || 'Erro ao atualizar prioridade'
    );
  } finally {
    prioritySaving.value = false;
  }
};

const getDealLabels = deal => {
  const source = deal?.labels || deal?.label_list || [];
  if (Array.isArray(source)) return source;
  if (typeof source === 'string') {
    return source
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};

const MAX_VISIBLE_DEAL_LABELS = 6;

const getVisibleDealLabels = deal =>
  getDealLabels(deal).slice(0, MAX_VISIBLE_DEAL_LABELS);

const getHiddenDealLabelsCount = deal =>
  Math.max(0, getDealLabels(deal).length - MAX_VISIBLE_DEAL_LABELS);

const getLabelColorByTitle = labelTitle => {
  const label = labels.value.find(item => item.title === labelTitle);
  return label?.color || '#64748B';
};

async function refreshDealById(dealId) {
  if (!dealId) return;
  await store.dispatch('deals/show', {
    accountId: accountId.value,
    dealId,
  });
}

const filteredLabels = computed(() => {
  const term = dealLabelsSearch.value.trim().toLowerCase();
  if (!term) return labels.value;
  return labels.value.filter(item => item.title?.toLowerCase().includes(term));
});

const toggleDealLabelsPopover = (deal, event) => {
  if (activeDealLabelsPopoverId.value === deal.id) {
    activeDealLabelsPopoverId.value = null;
    return;
  }
  activeDealActionsId.value = null;
  activeDealPopoverId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
  activeDealPriorityPopoverId.value = null;
  activeAssigneePopoverDealId.value = null;
  dealLabelsPopoverStyle.value = setSmallPopoverPosition(event, {
    width: 300,
    preferredHeight: 360,
  });
  dealLabelsSearch.value = '';
  activeDealLabelsPopoverId.value = deal.id;
};

const toggleLabelOnDeal = async (deal, labelTitle) => {
  labelsSaving.value = true;
  try {
    const currentLabels = [...new Set(getDealLabels(deal))];
    const nextLabels = currentLabels.includes(labelTitle)
      ? currentLabels.filter(item => item !== labelTitle)
      : [...currentLabels, labelTitle];

    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: deal.id,
      deal: { label_list: nextLabels },
    });
    await refreshDealById(deal.id);
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || 'Erro ao atualizar etiquetas'
    );
  } finally {
    labelsSaving.value = false;
  }
};

const openAddLabelModal = () => {
  showAddLabelModal.value = true;
};

const closeAddLabelModal = async () => {
  showAddLabelModal.value = false;
  await store.dispatch('labels/get').catch(() => null);
};

const openDealCustomFieldsModal = deal => {
  selectedDealForCustomFields.value = deal;
  selectedCustomAttributeKey.value = null;
  const attrs = deal.custom_attributes || {};
  const definitionKeys = new Set(
    dealAttributeDefinitions.value.map(item => item.attribute_key)
  );
  const keys = Object.keys(attrs).filter(key => {
    if (RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS.has(key)) return false;
    return definitionKeys.has(key);
  });
  dealCustomFieldKeys.value = keys;
  dealCustomFieldsDraft.value = keys.reduce((acc, key) => {
    acc[key] = attrs[key];
    return acc;
  }, {});
  showDealCustomFieldsModal.value = true;
  activeDealActionsId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
};

const closeDealCustomFieldsModal = () => {
  showDealCustomFieldsModal.value = false;
  selectedDealForCustomFields.value = null;
  selectedCustomAttributeKey.value = null;
  dealCustomFieldsDraft.value = {};
  dealCustomFieldKeys.value = [];
  activeCustomFieldListKey.value = null;
  customFieldListSearch.value = '';
};

const closeAddDealAttributeModal = () => {
  showAddDealAttributeModal.value = false;
  store.dispatch('attributes/get');
};

const openAddDealAttributeModal = () => {
  showAddDealAttributeModal.value = true;
  activeDealCustomFieldsPopoverId.value = null;
};

const addCustomFieldToDraft = () => {
  if (!selectedCustomAttributeKey.value) return;
  if (dealCustomFieldKeys.value.includes(selectedCustomAttributeKey.value)) {
    selectedCustomAttributeKey.value = null;
    return;
  }
  dealCustomFieldKeys.value.push(selectedCustomAttributeKey.value);
  dealCustomFieldsDraft.value = {
    ...dealCustomFieldsDraft.value,
    [selectedCustomAttributeKey.value]: '',
  };
  selectedCustomAttributeKey.value = null;
};

const removeCustomFieldFromDraft = key => {
  dealCustomFieldKeys.value = dealCustomFieldKeys.value.filter(
    item => item !== key
  );
  const next = { ...dealCustomFieldsDraft.value };
  delete next[key];
  dealCustomFieldsDraft.value = next;
};

const updateDraftCustomFieldValue = (key, value) => {
  dealCustomFieldsDraft.value = {
    ...dealCustomFieldsDraft.value,
    [key]: value,
  };
};

const getDraftCustomFieldDefinition = key => {
  return (
    dealAttributeDefinitions.value.find(item => item.attribute_key === key) ||
    null
  );
};

const getDraftCustomFieldType = key => {
  return getDraftCustomFieldDefinition(key)?.attribute_display_type || 'text';
};

const getDraftCustomFieldLabel = key => {
  return getDraftCustomFieldDefinition(key)?.attribute_display_name || key;
};

const getDraftCustomFieldOptions = key => {
  return getDraftCustomFieldDefinition(key)?.attribute_values || [];
};
const getDraftCustomFieldSelectedLabel = key => {
  const value = dealCustomFieldsDraft.value[key];
  if (!value)
    return t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.FIELD_PLACEHOLDER');
  return String(value);
};

const getFilteredDraftCustomFieldOptions = key => {
  const term = customFieldListSearch.value.trim().toLowerCase();
  const options = getDraftCustomFieldOptions(key);
  if (!term) return options;
  return options.filter(option => option.toLowerCase().includes(term));
};

const toggleCustomFieldListPopover = (key, event) => {
  if (activeCustomFieldListKey.value === key) {
    activeCustomFieldListKey.value = null;
    return;
  }
  const trigger = event?.currentTarget;
  const triggerRect = trigger?.getBoundingClientRect?.();
  customFieldListPopoverWidth.value = Math.max(
    220,
    Math.floor(triggerRect?.width || 320)
  );
  customFieldListPopoverStyle.value = setSmallPopoverPosition(event, {
    width: customFieldListPopoverWidth.value,
    preferredHeight: 320,
    minHeight: 220,
    openThreshold: 260,
    align: 'start',
  });
  customFieldListSearch.value = '';
  activeCustomFieldListKey.value = key;
};

const selectCustomFieldListOption = (key, value) => {
  updateDraftCustomFieldValue(key, value);
  activeCustomFieldListKey.value = null;
};

const saveDealCustomFields = async () => {
  if (!selectedDealForCustomFields.value) return;
  customFieldsSaving.value = true;
  try {
    const currentAttrs =
      selectedDealForCustomFields.value.custom_attributes || {};
    const reservedAttrs = Object.entries(currentAttrs).reduce(
      (acc, [key, value]) => {
        if (RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS.has(key)) acc[key] = value;
        return acc;
      },
      {}
    );

    const nextCustomFields = dealCustomFieldKeys.value.reduce((acc, key) => {
      const value = dealCustomFieldsDraft.value[key];
      if (isCustomFieldValuePresent(value)) {
        acc[key] = value;
      }
      return acc;
    }, {});

    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: selectedDealForCustomFields.value.id,
      deal: {
        custom_attributes: {
          ...reservedAttrs,
          ...nextCustomFields,
        },
      },
    });

    await refreshDealById(selectedDealForCustomFields.value.id);
    closeDealCustomFieldsModal();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.ERROR_SAVE')
    );
  } finally {
    customFieldsSaving.value = false;
  }
};

const addProduct = product => {
  selectedProducts.value.push({
    product_id: product.id,
    name: product.name,
    sku: product.sku,
    quantity: 1,
    unit_price: Number(product.price || 0),
  });
  productsView.value = 'summary';
};

const increaseQty = item => {
  item.quantity += 1;
};

const decreaseQty = item => {
  if (item.quantity <= 1) {
    removeProduct(item.product_id);
    return;
  }
  item.quantity -= 1;
};

function removeProduct(productId) {
  selectedProducts.value = selectedProducts.value.filter(
    item => Number(item.product_id) !== Number(productId)
  );
}

function setSmallPopoverPosition(
  event,
  {
    width = 300,
    preferredHeight = 320,
    minHeight = 220,
    openThreshold = 240,
    align = 'end',
  } = {}
) {
  const trigger = event?.currentTarget;
  if (!trigger) return { top: '120px', left: '24px' };
  const rect = trigger.getBoundingClientRect();
  const offset = 6;
  const margin = 12;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const availableBelow = viewportHeight - rect.bottom - offset - margin;
  const availableAbove = rect.top - offset - margin;
  const openUpwards =
    availableBelow < openThreshold && availableAbove > availableBelow;
  const maxHeight = Math.max(
    minHeight,
    Math.min(preferredHeight, openUpwards ? availableAbove : availableBelow)
  );
  const desiredLeft = align === 'start' ? rect.left : rect.right - width;
  const left = Math.max(
    margin,
    Math.min(desiredLeft, viewportWidth - width - margin)
  );
  const anchorTop = openUpwards ? rect.top - offset : rect.bottom + offset;
  return {
    top: `${Math.max(margin, anchorTop)}px`,
    left: `${left}px`,
    maxHeight: `${Math.floor(maxHeight)}px`,
    transform: openUpwards ? 'translateY(-100%)' : 'none',
    transformOrigin: openUpwards ? 'bottom right' : 'top right',
  };
}

const setDealPopoverPosition = event => {
  dealPopoverStyle.value = setSmallPopoverPosition(event, {
    width: 380,
    preferredHeight: 560,
    minHeight: 300,
    openThreshold: 360,
    align: 'start',
  });
};

const openDealProductsPopover = (deal, event) => {
  if (activeDealPopoverId.value === deal.id) {
    closeDealProductsPopover();
    return;
  }
  activeDealActionsId.value = null;
  activeDealPriorityPopoverId.value = null;
  activeDealLabelsPopoverId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
  activeAssigneePopoverDealId.value = null;
  setDealPopoverPosition(event);
  activeDealPopoverId.value = deal.id;
  dealProductsView.value = 'summary';
  dealProductSearch.value = '';
  showCreateDealProductForm.value = false;
  dealNewProduct.value = { name: '', sku: '', price: '' };

  const productsFromDeal = deal.products || [];
  dealSelectedProducts.value = productsFromDeal.map(item => ({
    deal_product_id: item.id || null,
    product_id: item.product_id,
    name: item.name || item.product?.name || 'Produto',
    sku: item.sku || item.product?.sku || '',
    quantity: Number(item.quantity || 1),
    unit_price: Number(item.unit_price || 0),
  }));

  const attrs = deal.custom_attributes || {};
  dealCouponCode.value = attrs.coupon_code || '';
  dealManualDiscount.value = String(attrs.manual_discount || 0);
  dealSurcharge.value = String(attrs.surcharge || 0);
  dealShipping.value = String(attrs.shipping || 0);
  dealSelectedCoupon.value = null;

  if (dealCouponCode.value) {
    const normalized = dealCouponCode.value.toUpperCase();
    dealSelectedCoupon.value = coupons.value.find(
      item => item.active && item.code?.toUpperCase() === normalized
    );
  }
};

function closeDealProductsPopover() {
  activeDealPopoverId.value = null;
  showCreateDealProductForm.value = false;
  dealNewProduct.value = { name: '', sku: '', price: '' };
}

const syncDealProducts = async ({
  dealId,
  existingProducts,
  selectedItems,
}) => {
  const normalizedExisting = (existingProducts || []).map(item => ({
    deal_product_id: item.id,
    product_id: item.product_id,
    quantity: Number(item.quantity || 1),
    unit_price: Number(item.unit_price || 0),
  }));
  const selectedByProduct = new Map(
    selectedItems.map(item => [Number(item.product_id), item])
  );
  const existingByProduct = new Map(
    normalizedExisting.map(item => [Number(item.product_id), item])
  );
  const deletes = normalizedExisting
    .filter(item => !selectedByProduct.has(Number(item.product_id)))
    .map(item =>
      DealProductsAPI.delete(accountId.value, dealId, item.deal_product_id)
    );
  const updates = normalizedExisting
    .filter(item => selectedByProduct.has(Number(item.product_id)))
    .filter(item => {
      const selected = selectedByProduct.get(Number(item.product_id));
      return (
        Number(selected.quantity) !== Number(item.quantity) ||
        Number(selected.unit_price) !== Number(item.unit_price)
      );
    })
    .map(item => {
      const selected = selectedByProduct.get(Number(item.product_id));
      return DealProductsAPI.update(
        accountId.value,
        dealId,
        item.deal_product_id,
        {
          quantity: Number(selected.quantity),
          unit_price: Number(selected.unit_price),
        }
      );
    });
  const creates = selectedItems
    .filter(item => !existingByProduct.has(Number(item.product_id)))
    .map(item =>
      DealProductsAPI.create(accountId.value, dealId, {
        product_id: item.product_id,
        quantity: Number(item.quantity),
        unit_price: Number(item.unit_price),
      })
    );

  await Promise.all([...deletes, ...updates, ...creates]);
};

const addProductToDealPopover = product => {
  dealSelectedProducts.value.push({
    deal_product_id: null,
    product_id: product.id,
    name: product.name,
    sku: product.sku,
    quantity: 1,
    unit_price: Number(product.price || 0),
  });
  dealProductsView.value = 'summary';
};

const increaseDealQty = item => {
  item.quantity += 1;
};

const decreaseDealQty = item => {
  if (item.quantity <= 1) {
    removeDealProduct(item.product_id);
    return;
  }
  item.quantity -= 1;
};

function removeDealProduct(productId) {
  dealSelectedProducts.value = dealSelectedProducts.value.filter(
    item => Number(item.product_id) !== Number(productId)
  );
}

const applyDealCoupon = () => {
  const normalized = dealCouponCode.value.trim().toUpperCase();
  if (!normalized) {
    dealSelectedCoupon.value = null;
    return;
  }

  const coupon = coupons.value.find(
    item => item.active && item.code?.toUpperCase() === normalized
  );
  if (!coupon) {
    dealSelectedCoupon.value = null;
    useAlert(t('DEALS.CONVERSATION.PRODUCTS.COUPON_NOT_FOUND'));
    return;
  }

  dealSelectedCoupon.value = coupon;
  dealManualDiscount.value = '0';
};

const saveDealProducts = async deal => {
  dealPopoverSaving.value = true;
  try {
    const safeAmount = toSafeNumber(dealFinalTotal.value, 0);
    await syncDealProducts({
      dealId: deal.id,
      existingProducts: deal.products || [],
      selectedItems: dealSelectedProducts.value,
    });

    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: deal.id,
      deal: {
        amount: safeAmount,
        custom_attributes: {
          ...(deal.custom_attributes || {}),
          subtotal_products: toSafeNumber(dealSubtotalProducts.value, 0),
          coupon_code: dealSelectedCoupon.value?.code || null,
          coupon_discount: toSafeNumber(dealCouponDiscount.value, 0),
          manual_discount: toSafeNumber(dealParsedManualDiscount.value, 0),
          surcharge: toSafeNumber(dealParsedSurcharge.value, 0),
          shipping: toSafeNumber(dealParsedShipping.value, 0),
          calculated_total: safeAmount,
        },
      },
    });

    await refreshDealById(deal.id);

    closeDealProductsPopover();
  } catch (error) {
    const message =
      error?.response?.data?.errors?.[0] ||
      t('DEALS.CONVERSATION.PRODUCTS.ERROR_SAVE_DEAL_PRODUCTS');
    useAlert(message);
  } finally {
    dealPopoverSaving.value = false;
  }
};

const toggleDealActions = dealId => {
  if (activeDealActionsId.value === dealId) {
    activeDealActionsId.value = null;
    return;
  }
  activeAssigneePopoverDealId.value = null;
  activeDealActionsId.value = dealId;
  activeDealPopoverId.value = null;
};

const fillFormWithDeal = deal => {
  selectedPipelineId.value = deal.pipeline_id || null;
  selectedStageId.value = deal.pipeline_stage_id || null;
  title.value = (deal.title || '').trim();
  description.value = deal.description || '';
  selectedContactId.value =
    Number(deal.contact_id) ||
    Number(props.currentChat?.meta?.sender?.id) ||
    null;
  selectedConversationId.value =
    getDealConversationRouteId(deal) || Number(props.conversationId) || null;
  closeDate.value = deal.close_date ? deal.close_date.slice(0, 10) : '';
  selectedProducts.value = (deal.products || []).map(item => ({
    deal_product_id: item.id || null,
    product_id: item.product_id,
    name: item.name || item.product?.name || 'Produto',
    sku: item.sku || item.product?.sku || '',
    quantity: Number(item.quantity || 1),
    unit_price: Number(item.unit_price || 0),
  }));
  showProductsPopover.value = false;
  productsView.value = 'summary';
  productSearch.value = '';
  showCreateProductForm.value = false;
  newProduct.value = { name: '', sku: '', price: '' };

  const attrs = deal.custom_attributes || {};
  startDate.value = attrs.start_date
    ? String(attrs.start_date).slice(0, 10)
    : '';
  const customAgentIds = Array.isArray(attrs.responsible_agent_ids)
    ? attrs.responsible_agent_ids
        .map(id => Number(id))
        .filter(id => !Number.isNaN(id))
    : [];
  const nativeAssignee = Number(deal.assignee_id || deal.assignee?.id);
  selectedAgentIds.value = [
    ...new Set(
      [
        ...customAgentIds,
        Number.isNaN(nativeAssignee) ? null : nativeAssignee,
      ].filter(Boolean)
    ),
  ];
  if (Array.isArray(deal.labels)) {
    selectedLabelTitles.value = [...deal.labels];
  } else if (Array.isArray(deal.label_list)) {
    selectedLabelTitles.value = [...deal.label_list];
  } else {
    selectedLabelTitles.value = [];
  }
  couponCode.value = attrs.coupon_code || '';
  manualDiscount.value = String(attrs.manual_discount || 0);
  surcharge.value = String(attrs.surcharge || 0);
  shipping.value = String(attrs.shipping || 0);
  selectedCoupon.value = null;
  if (couponCode.value) {
    const normalized = couponCode.value.toUpperCase();
    selectedCoupon.value = coupons.value.find(
      item => item.active && item.code?.toUpperCase() === normalized
    );
  }
};

const openEditModal = async deal => {
  await loadPipelineInboxPermissions();
  editingDealId.value = deal.id;
  selectedDealForForm.value = deal;
  fillFormWithDeal(deal);
  showCreateModal.value = true;
  activeDealActionsId.value = null;
};

const openLoseDealModal = deal => {
  selectedDealForLoss.value = deal;
  selectedLossReasonId.value = deal.deal_loss_reason_id || null;
  lossJustification.value = deal.loss_justification || '';
  showLoseDealModal.value = true;
  activeDealActionsId.value = null;
};

const isWonOrLostDeal = deal =>
  ['won', 'lost'].includes(String(deal?.status || '').toLowerCase());

const closeLoseDealModal = () => {
  showLoseDealModal.value = false;
  selectedDealForLoss.value = null;
  selectedLossReasonId.value = null;
  lossJustification.value = '';
};

const confirmLoseDeal = async () => {
  if (!selectedDealForLoss.value) return;
  if (!selectedLossReasonId.value) {
    useAlert(t('DEALS.CONVERSATION.LOSS_MODAL.REASON_REQUIRED'));
    return;
  }
  if (
    selectedLossReason.value?.requires_justification &&
    !lossJustification.value.trim()
  ) {
    useAlert(t('DEALS.CONVERSATION.LOSS_MODAL.JUSTIFICATION_REQUIRED_ALERT'));
    return;
  }

  loseDealLoading.value = true;
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: selectedDealForLoss.value.id,
      deal: {
        status: 'lost',
        deal_loss_reason_id: selectedLossReasonId.value,
        loss_justification: lossJustification.value.trim() || null,
      },
    });
    await refreshDealById(selectedDealForLoss.value.id);
    closeLoseDealModal();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('DEALS.CONVERSATION.LOSS_MODAL.ERROR')
    );
  } finally {
    loseDealLoading.value = false;
  }
};

function openDeleteDealPopup(deal) {
  selectedDealForDelete.value = deal;
  showDeleteConfirmationPopup.value = true;
  activeDealActionsId.value = null;
}

const closeDeleteDealPopup = () => {
  showDeleteConfirmationPopup.value = false;
  selectedDealForDelete.value = null;
};

const confirmDeleteDeal = async () => {
  if (!selectedDealForDelete.value) return;
  deleteDealLoading.value = true;
  try {
    await store.dispatch('deals/delete', {
      accountId: accountId.value,
      dealId: selectedDealForDelete.value.id,
    });
    closeDeleteDealPopup();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('DEALS.CONVERSATION.DELETE_MODAL.ERROR')
    );
  } finally {
    deleteDealLoading.value = false;
  }
};

const handleDealAction = async (deal, action) => {
  if (action === 'delete') {
    openDeleteDealPopup(deal);
    return;
  }
  if (action === 'edit') {
    await openEditModal(deal);
    return;
  }
  if (action === 'custom_fields') {
    openDealCustomFieldsModal(deal);
    return;
  }
  if (action === 'lost') {
    openLoseDealModal(deal);
    return;
  }

  if (action === 'won') {
    activeDealActionsId.value = null;
    try {
      await store.dispatch('deals/update', {
        accountId: accountId.value,
        dealId: deal.id,
        deal: { status: action },
      });
      await refreshDealById(deal.id);
      return;
    } catch (error) {
      useAlert(
        error?.response?.data?.errors?.[0] ||
          t('DEALS.CONVERSATION.DEAL_MODAL.ERROR_UPDATE')
      );
      return;
    }
  }

  if (action === 'restore') {
    activeDealActionsId.value = null;
    try {
      await store.dispatch('deals/update', {
        accountId: accountId.value,
        dealId: deal.id,
        deal: {
          status: 'open',
          deal_loss_reason_id: null,
          loss_justification: null,
        },
      });
      await refreshDealById(deal.id);
      return;
    } catch (error) {
      useAlert(
        error?.response?.data?.errors?.[0] ||
          t('DEALS.CONVERSATION.DEAL_MODAL.ERROR_UPDATE')
      );
      return;
    }
  }

  activeDealActionsId.value = null;
  useAlert(t('DEALS.CONVERSATION.ACTIONS.COMING_SOON'));
};

const applyCoupon = () => {
  const normalized = couponCode.value.trim().toUpperCase();
  if (!normalized) {
    selectedCoupon.value = null;
    return;
  }

  const coupon = coupons.value.find(
    item => item.active && item.code?.toUpperCase() === normalized
  );

  if (!coupon) {
    selectedCoupon.value = null;
    useAlert(t('DEALS.CONVERSATION.PRODUCTS.COUPON_NOT_FOUND'));
    return;
  }

  selectedCoupon.value = coupon;
  manualDiscount.value = '0';
};

const createProductQuickly = async () => {
  if (!newProduct.value.name || !newProduct.value.sku) {
    useAlert(t('DEALS.CONVERSATION.PRODUCTS.FILL_NAME_SKU_ERROR'));
    return;
  }

  try {
    await store.dispatch('products/create', {
      accountId: accountId.value,
      product: {
        name: newProduct.value.name,
        sku: newProduct.value.sku,
        price: Number(newProduct.value.price || 0),
        active: true,
      },
    });
    await store.dispatch('products/get', { accountId: accountId.value });
    showCreateProductForm.value = false;
    newProduct.value = { name: '', sku: '', price: '' };
  } catch (error) {
    const message =
      error?.response?.data?.errors?.[0] ||
      t('DEALS.CONVERSATION.PRODUCTS.ERROR_CREATE_PRODUCT');
    useAlert(message);
  }
};

const createDealProductQuickly = async () => {
  if (!dealNewProduct.value.name || !dealNewProduct.value.sku) {
    useAlert(t('DEALS.CONVERSATION.PRODUCTS.FILL_NAME_SKU_ERROR'));
    return;
  }

  try {
    await store.dispatch('products/create', {
      accountId: accountId.value,
      product: {
        name: dealNewProduct.value.name,
        sku: dealNewProduct.value.sku,
        price: Number(dealNewProduct.value.price || 0),
        active: true,
      },
    });
    await store.dispatch('products/get', { accountId: accountId.value });
    showCreateDealProductForm.value = false;
    dealNewProduct.value = { name: '', sku: '', price: '' };
  } catch (error) {
    const message =
      error?.response?.data?.errors?.[0] ||
      t('DEALS.CONVERSATION.PRODUCTS.ERROR_CREATE_PRODUCT');
    useAlert(message);
  }
};

const createDeal = async () => {
  await loadPipelineInboxPermissions();

  if (!selectedPipelineId.value || !selectedStageId.value) {
    useAlert(t('DEALS.CONVERSATION.DEAL_MODAL.SELECT_PIPELINE_STAGE'));
    return;
  }

  const selectedPipelineData = selectedPipeline.value;
  const inboxIds = getPipelineInboxIds(selectedPipelineData);
  const inboxId = currentConversationInboxId.value;
  if (inboxId === null || !inboxIds.includes(inboxId)) {
    useAlert(t('DEALS.CONVERSATION.DEAL_MODAL.INBOX_NOT_ALLOWED'));
    return;
  }

  creating.value = true;
  let createdDealId = null;
  try {
    if (isEditingMode.value) {
      const currentDeal = conversationDeals.value.find(
        item => Number(item.id) === Number(editingDealId.value)
      );
      await store.dispatch('deals/update', {
        accountId: accountId.value,
        dealId: editingDealId.value,
        deal: {
          title:
            title.value?.trim() || t('DEALS.CONVERSATION.FALLBACK_DEAL_TITLE'),
          description: description.value?.trim() || null,
          pipeline_id: selectedPipelineId.value,
          pipeline_stage_id: selectedStageId.value,
          contact_id:
            selectedContactId.value ||
            props.currentChat?.meta?.sender?.id ||
            null,
          conversation_id: selectedConversationId.value || null,
          assignee_id: selectedAgentIds.value[0] || null,
          label_list: selectedLabelTitles.value,
          amount: finalTotal.value,
          close_date: closeDate.value || null,
          amount_from_products: false,
          custom_attributes: {
            ...(currentDeal?.custom_attributes || {}),
            start_date: startDate.value || null,
            responsible_agent_ids: selectedAgentIds.value,
            subtotal_products: subtotalProducts.value,
            coupon_code: selectedCoupon.value?.code || null,
            coupon_discount: couponDiscount.value,
            manual_discount: parsedManualDiscount.value,
            surcharge: parsedSurcharge.value,
            shipping: parsedShipping.value,
            calculated_total: finalTotal.value,
          },
        },
      });
      await syncDealProducts({
        dealId: editingDealId.value,
        existingProducts: currentDeal?.products || [],
        selectedItems: selectedProducts.value,
      });
    } else {
      const response = await store.dispatch('deals/create', {
        accountId: accountId.value,
        deal: {
          title:
            title.value?.trim() || t('DEALS.CONVERSATION.FALLBACK_DEAL_TITLE'),
          description: description.value?.trim() || null,
          pipeline_id: selectedPipelineId.value,
          pipeline_stage_id: selectedStageId.value,
          conversation_id:
            selectedConversationId.value || Number(props.conversationId),
          contact_id:
            selectedContactId.value ||
            props.currentChat?.meta?.sender?.id ||
            null,
          assignee_id: selectedAgentIds.value[0] || null,
          label_list: selectedLabelTitles.value,
          amount: finalTotal.value,
          close_date: closeDate.value || null,
          amount_from_products: false,
          custom_attributes: {
            start_date: startDate.value || null,
            responsible_agent_ids: selectedAgentIds.value,
            subtotal_products: subtotalProducts.value,
            coupon_code: selectedCoupon.value?.code || null,
            coupon_discount: couponDiscount.value,
            manual_discount: parsedManualDiscount.value,
            surcharge: parsedSurcharge.value,
            shipping: parsedShipping.value,
            calculated_total: finalTotal.value,
          },
        },
      });

      const dealData = response?.data || response;
      createdDealId = dealData?.id;

      if (createdDealId && selectedProducts.value.length > 0) {
        await Promise.all(
          selectedProducts.value.map(item =>
            DealProductsAPI.create(accountId.value, createdDealId, {
              product_id: item.product_id,
              quantity: item.quantity,
              unit_price: item.unit_price,
            })
          )
        );
      }
    }

    const targetDealId = isEditingMode.value
      ? editingDealId.value
      : createdDealId;
    await refreshDealById(targetDealId);

    closeCreateModal();
  } catch (error) {
    const message =
      error?.response?.data?.errors?.[0] ||
      t('DEALS.CONVERSATION.DEAL_MODAL.ERROR_CREATE');
    useAlert(message);
  } finally {
    creating.value = false;
  }
};

const modalPipelineId = computed(() => {
  return (
    Number(selectedDealForForm.value?.pipeline_id) ||
    Number(selectedPipelineId.value) ||
    Number(pipelineOptions.value[0]?.id) ||
    null
  );
});

const modalStages = computed(() => {
  const pipelineId = Number(
    selectedDealForForm.value?.pipeline_id || selectedPipelineId.value
  );
  const selected = pipelines.value.find(
    pipeline => Number(pipeline.id) === pipelineId
  );
  const stages = selected?.stages || [];
  return [...stages].sort((a, b) => (a.position || 0) - (b.position || 0));
});

const dealModalInitialValues = computed(() => ({
  pipeline_id:
    Number(selectedPipelineId.value) ||
    Number(pipelineOptions.value[0]?.id) ||
    null,
  pipeline_stage_id:
    Number(selectedStageId.value) ||
    Number(stageOptions.value[0]?.value) ||
    null,
  contact_id:
    Number(selectedContactId.value) ||
    Number(props.currentChat?.meta?.sender?.id) ||
    null,
  conversation_id:
    Number(selectedConversationId.value) ||
    Number(props.conversationId) ||
    null,
  contact_label:
    props.currentChat?.meta?.sender?.name ||
    props.currentChat?.meta?.sender?.identifier ||
    null,
  conversation_label: `#${Number(selectedConversationId.value) || Number(props.conversationId) || ''} - ${
    props.currentChat?.meta?.sender?.name ||
    props.currentChat?.meta?.sender?.identifier ||
    'Conversa'
  }`,
}));

const submitDealFromModal = async dealData => {
  await loadPipelineInboxPermissions();
  if (!dealData) return;

  const pipelineId = Number(dealData.pipeline_id);
  const selectedPipelineData = pipelines.value.find(
    item => Number(item.id) === pipelineId
  );
  const inboxIds = getPipelineInboxIds(selectedPipelineData);
  const inboxId = currentConversationInboxId.value;
  if (inboxId === null || !inboxIds.includes(inboxId)) {
    useAlert(t('DEALS.CONVERSATION.DEAL_MODAL.INBOX_NOT_ALLOWED'));
    return;
  }

  creating.value = true;
  let createdDealId = null;
  try {
    const { selected_products: selectedProductsFromModal = [], ...payload } =
      dealData;

    if (isEditingMode.value) {
      const currentDeal = conversationDeals.value.find(
        item => Number(item.id) === Number(editingDealId.value)
      );
      await store.dispatch('deals/update', {
        accountId: accountId.value,
        dealId: editingDealId.value,
        deal: {
          ...payload,
          conversation_id:
            payload.conversation_id || Number(props.conversationId),
          contact_id:
            payload.contact_id || props.currentChat?.meta?.sender?.id || null,
        },
      });
      await syncDealProducts({
        dealId: editingDealId.value,
        existingProducts: currentDeal?.products || [],
        selectedItems: selectedProductsFromModal,
      });
    } else {
      const response = await store.dispatch('deals/create', {
        accountId: accountId.value,
        deal: {
          ...payload,
          conversation_id:
            payload.conversation_id || Number(props.conversationId),
          contact_id:
            payload.contact_id || props.currentChat?.meta?.sender?.id || null,
        },
      });
      const dealResponse = response?.data || response;
      createdDealId = dealResponse?.id;
      if (createdDealId && selectedProductsFromModal.length > 0) {
        await Promise.all(
          selectedProductsFromModal.map(item =>
            DealProductsAPI.create(accountId.value, createdDealId, {
              product_id: item.product_id,
              quantity: item.quantity,
              unit_price: item.unit_price,
            })
          )
        );
      }
    }

    const targetDealId = isEditingMode.value
      ? editingDealId.value
      : createdDealId;
    await refreshDealById(targetDealId);
    closeCreateModal();
  } catch (error) {
    const message =
      error?.response?.data?.errors?.[0] ||
      t('DEALS.CONVERSATION.DEAL_MODAL.ERROR_CREATE');
    useAlert(message);
  } finally {
    creating.value = false;
  }
};
</script>

<template>
  <div class="space-y-3">
    <div
      v-if="loading"
      class="flex items-center justify-center p-4 gap-2 text-sm text-n-slate-11"
    >
      <span>{{ t('DEALS.CONVERSATION.LOADING') }}</span>
      <Spinner class="size-4" />
    </div>

    <div
      v-else-if="!conversationDeals.length"
      class="text-sm text-n-slate-11 px-2 py-1"
    >
      {{ t('DEALS.CONVERSATION.EMPTY') }}
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="deal in conversationDeals"
        :key="deal.id"
        class="border border-n-weak rounded-lg p-3 bg-n-alpha-1 relative"
      >
        <div class="flex items-center justify-between">
          <p class="text-sm font-semibold text-n-slate-12">
            <span class="inline-flex items-center gap-1.5">
              <span
                v-if="deal.status === 'won'"
                class="i-lucide-circle-check size-4 text-green-600"
                :title="t('DEALS.STATUS.WON')"
              />
              <span
                v-else-if="deal.status === 'lost'"
                class="i-lucide-circle-x size-4 text-red-600"
                :title="t('DEALS.STATUS.LOST')"
              />
              <span>{{ getDealDisplayTitle(deal) }}</span>
            </span>
          </p>
          <button
            type="button"
            class="p-1.5 rounded hover:bg-n-alpha-2"
            data-deal-actions-trigger
            @click.stop="toggleDealActions(deal.id)"
          >
            <span class="i-lucide-ellipsis size-4 text-n-slate-11" />
          </button>
        </div>

        <div
          v-if="activeDealActionsId === deal.id"
          data-deal-actions-menu
          class="absolute z-30 top-10 right-2 w-56 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg py-1"
        >
          <button
            type="button"
            class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
            @click.stop="handleDealAction(deal, 'activity')"
          >
            <span class="i-lucide-calendar-plus size-4 text-n-slate-11" />{{
              t('DEALS.CONVERSATION.ACTIONS.CREATE_ACTIVITY')
            }}
          </button>
          <button
            type="button"
            class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
            @click.stop="handleDealAction(deal, 'edit')"
          >
            <span class="i-lucide-pencil size-4 text-n-slate-11" />{{
              t('DEALS.CONVERSATION.ACTIONS.EDIT_DEAL')
            }}
          </button>
          <button
            type="button"
            class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
            @click.stop="handleDealAction(deal, 'custom_fields')"
          >
            <span class="i-lucide-code size-4 text-n-slate-11" />{{
              t('DEALS.CONVERSATION.ACTIONS.ADD_CUSTOM_ATTRIBUTES')
            }}
          </button>
          <button
            v-if="isWonOrLostDeal(deal)"
            type="button"
            class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
            @click.stop="handleDealAction(deal, 'restore')"
          >
            <span class="i-lucide-rotate-ccw size-4 text-n-slate-11" />{{
              t('DEALS.CONVERSATION.ACTIONS.RESTORE_DEAL')
            }}
          </button>
          <template v-else>
            <button
              type="button"
              class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
              @click.stop="handleDealAction(deal, 'won')"
            >
              <span class="i-lucide-check size-4 text-n-slate-11" />{{
                t('DEALS.CONVERSATION.ACTIONS.WIN_DEAL')
              }}
            </button>
            <button
              type="button"
              class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
              @click.stop="handleDealAction(deal, 'lost')"
            >
              <span class="i-lucide-x size-4 text-n-slate-11" />{{
                t('DEALS.CONVERSATION.ACTIONS.LOSE_DEAL')
              }}
            </button>
          </template>
          <button
            type="button"
            class="w-full text-left px-3 py-2 text-sm text-ruby-10 hover:bg-ruby-2 inline-flex items-center gap-2"
            @click.stop="handleDealAction(deal, 'delete')"
          >
            <span class="i-lucide-trash-2 size-4" />{{
              t('DEALS.CONVERSATION.ACTIONS.DELETE_DEAL')
            }}
          </button>
        </div>

        <button
          type="button"
          class="text-xs text-n-brand mt-1 hover:underline inline-flex items-center gap-1"
          data-deal-products-trigger
          :title="`${t('DEALS.CONVERSATION.DEAL_LIST.PRODUCTS')}: ${getProductSummary(deal)}`"
          @click.stop.prevent="openDealProductsPopover(deal, $event)"
        >
          <span class="i-lucide-package size-3.5" />
          {{ getProductSummary(deal) }}
        </button>

        <div class="mt-3 space-y-1.5 text-xs text-n-slate-11">
          <div
            class="flex items-center gap-2"
            :title="`${t('DEALS.CONVERSATION.DEAL_LIST.AMOUNT')}: ${formatCurrency(getDealDisplayAmount(deal))}`"
          >
            <span class="i-lucide-circle-dollar-sign size-4 text-n-slate-10" />
            <span>{{ formatCurrency(getDealDisplayAmount(deal)) }}</span>
          </div>
          <div
            class="flex items-center gap-2"
            :title="`${t('DEALS.CONVERSATION.DEAL_LIST.CLOSE_DATE')}: ${formatDate(deal.close_date)}`"
          >
            <span class="i-lucide-calendar size-4 text-n-slate-10" />
            <span>{{ formatDate(deal.close_date) }}</span>
          </div>
          <div
            class="flex items-center gap-2"
            :title="t('DEALS.CONVERSATION.NO_ACTIVITIES')"
          >
            <span class="i-lucide-calendar-plus size-4 text-n-slate-10" />
            <span>{{ t('DEALS.CONVERSATION.NO_ACTIVITIES') }}</span>
          </div>
          <div
            class="flex items-center gap-2"
            :title="`${t('DEALS.CONVERSATION.DEAL_LIST.PIPELINE')}: ${getPipelineName(deal)}`"
          >
            <span class="i-lucide-funnel size-4 text-n-slate-10" />
            <span>{{ getPipelineName(deal) }}</span>
          </div>
        </div>

        <div class="flex gap-1 mt-2">
          <button
            v-for="(stage, index) in getPipelineStages(deal)"
            :key="`${deal.id}-${stage.id}`"
            class="h-2 flex-1 rounded-full transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="stageUpdatingDealId === deal.id"
            :title="`${t('DEALS.CONVERSATION.DEAL_LIST.STAGE')}: ${getStageTooltip(stage, deal)}`"
            :style="getStageProgressStyle(stage, index, deal)"
            @click="setDealStage(deal, stage.id)"
          />
        </div>

        <teleport to="body">
          <div
            v-if="activeDealPopoverId === deal.id"
            data-deal-products-popover
            class="fixed z-[1000] flex max-h-[calc(100vh-1.5rem)] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-xl"
            :style="dealPopoverStyle"
          >
            <template v-if="dealProductsView === 'summary'">
              <div class="flex-1 overflow-y-auto p-3 space-y-3">
                <div
                  v-if="!dealSelectedProducts.length"
                  class="text-center py-6 text-sm text-n-slate-12"
                >
                  {{ t('DEALS.CONVERSATION.PRODUCTS.EMPTY') }}
                </div>

                <div v-else class="space-y-2">
                  <div
                    v-for="item in dealSelectedProducts"
                    :key="item.product_id"
                    class="border-b border-n-weak pb-2 last:border-0 last:pb-0"
                  >
                    <div class="flex items-center justify-between gap-2">
                      <p class="text-sm text-n-slate-12 truncate">
                        {{ item.name }}
                      </p>
                      <span class="text-sm font-medium text-n-slate-12">{{
                        formatCurrency(item.unit_price)
                      }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-2 mt-1">
                      <p class="text-xs text-n-slate-11 truncate">
                        {{ item.sku || '-' }}
                      </p>
                      <div
                        class="flex items-center border border-n-weak rounded"
                      >
                        <button
                          type="button"
                          class="px-2 py-0.5 text-n-slate-12"
                          @click.stop.prevent="decreaseDealQty(item)"
                        >
                          -
                        </button>
                        <span class="px-2 text-sm text-n-slate-12">{{
                          item.quantity
                        }}</span>
                        <button
                          type="button"
                          class="px-2 py-0.5 text-n-slate-12"
                          @click.stop.prevent="increaseDealQty(item)"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="text-right">
                  <button
                    type="button"
                    class="text-sm text-n-brand"
                    @click.stop.prevent="dealProductsView = 'catalog'"
                  >
                    {{ t('DEALS.CONVERSATION.PRODUCTS.ADD_PRODUCT') }}
                  </button>
                </div>

                <div
                  class="border-t border-n-weak pt-2 space-y-1.5 text-sm text-n-slate-12"
                >
                  <div class="flex justify-between">
                    <span>{{
                      t('DEALS.CONVERSATION.PRODUCTS.TOTAL_PRODUCTS')
                    }}</span>
                    <span class="font-medium">{{
                      formatCurrency(dealSubtotalProducts)
                    }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-2">
                    <span>{{ t('DEALS.CONVERSATION.PRODUCTS.COUPON') }}</span>
                    <input
                      v-model="dealCouponCode"
                      type="text"
                      class="w-40 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                      :placeholder="
                        t('DEALS.CONVERSATION.PRODUCTS.COUPON_PLACEHOLDER')
                      "
                      @blur="applyDealCoupon"
                    />
                  </div>
                  <div class="flex items-center justify-between gap-2">
                    <span>{{ t('DEALS.CONVERSATION.PRODUCTS.DISCOUNT') }}</span>
                    <input
                      v-model="dealManualDiscount"
                      type="number"
                      min="0"
                      step="0.01"
                      class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                    />
                  </div>
                  <div class="flex items-center justify-between gap-2">
                    <span>{{
                      t('DEALS.CONVERSATION.PRODUCTS.SURCHARGE')
                    }}</span>
                    <input
                      v-model="dealSurcharge"
                      type="number"
                      min="0"
                      step="0.01"
                      class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                    />
                  </div>
                  <div class="flex items-center justify-between gap-2">
                    <span>{{ t('DEALS.CONVERSATION.PRODUCTS.SHIPPING') }}</span>
                    <input
                      v-model="dealShipping"
                      type="number"
                      min="0"
                      step="0.01"
                      class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                    />
                  </div>
                </div>
              </div>

              <div
                class="shrink-0 border-t border-n-weak bg-n-solid-1 p-3 space-y-2"
              >
                <div
                  class="flex items-center justify-between text-base font-semibold text-n-slate-12"
                >
                  <span>{{ t('DEALS.CONVERSATION.PRODUCTS.TOTAL') }}</span>
                  <span>{{ formatCurrency(dealFinalTotal) }}</span>
                </div>

                <div class="flex justify-end gap-2">
                  <Button
                    slate
                    faded
                    xs
                    :label="t('DEALS.CONVERSATION.PRODUCTS.CANCEL')"
                    @click="closeDealProductsPopover"
                  />
                  <Button
                    xs
                    :is-loading="dealPopoverSaving"
                    :label="t('DEALS.CONVERSATION.PRODUCTS.SAVE')"
                    @click="saveDealProducts(deal)"
                  />
                </div>
              </div>
            </template>

            <template v-else>
              <div class="flex-1 overflow-y-auto p-3 space-y-3">
                <input
                  v-model="dealProductSearch"
                  type="text"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                  :placeholder="
                    t('DEALS.CONVERSATION.PRODUCTS.SEARCH_PLACEHOLDER')
                  "
                />

                <div
                  class="max-h-56 overflow-y-auto border border-n-weak rounded-lg py-1"
                >
                  <button
                    v-for="product in filteredDealCatalogProducts"
                    :key="product.id"
                    type="button"
                    class="w-full text-left px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                    @click.stop.prevent="addProductToDealPopover(product)"
                  >
                    {{ product.name }}
                  </button>
                  <p
                    v-if="!filteredDealCatalogProducts.length"
                    class="px-3 py-2 text-sm text-n-slate-12"
                  >
                    {{ t('DEALS.CONVERSATION.PRODUCTS.EMPTY_FOUND') }}
                  </p>
                </div>

                <div class="flex items-center justify-between">
                  <button
                    type="button"
                    class="text-sm text-n-slate-12"
                    @click.stop.prevent="dealProductsView = 'summary'"
                  >
                    {{ t('DEALS.CONVERSATION.PRODUCTS.BACK') }}
                  </button>
                  <button
                    type="button"
                    class="text-sm text-n-brand"
                    @click.stop.prevent="
                      showCreateDealProductForm = !showCreateDealProductForm
                    "
                  >
                    {{ t('DEALS.CONVERSATION.PRODUCTS.CREATE') }}
                  </button>
                </div>

                <div
                  v-if="showCreateDealProductForm"
                  class="space-y-2 border border-n-weak rounded-lg p-2"
                >
                  <input
                    v-model="dealNewProduct.name"
                    type="text"
                    class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                    :placeholder="
                      t(
                        'DEALS.CONVERSATION.PRODUCTS.NEW_PRODUCT_NAME_PLACEHOLDER'
                      )
                    "
                  />
                  <input
                    v-model="dealNewProduct.sku"
                    type="text"
                    class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                    :placeholder="
                      t(
                        'DEALS.CONVERSATION.PRODUCTS.NEW_PRODUCT_SKU_PLACEHOLDER'
                      )
                    "
                  />
                  <input
                    v-model="dealNewProduct.price"
                    type="number"
                    min="0"
                    step="0.01"
                    class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                    :placeholder="
                      t(
                        'DEALS.CONVERSATION.PRODUCTS.NEW_PRODUCT_PRICE_PLACEHOLDER'
                      )
                    "
                  />
                  <div class="flex justify-end gap-2">
                    <Button
                      slate
                      faded
                      xs
                      :label="t('DEALS.CONVERSATION.PRODUCTS.CANCEL')"
                      @click.stop.prevent="showCreateDealProductForm = false"
                    />
                    <Button
                      xs
                      :label="t('DEALS.CONVERSATION.PRODUCTS.SAVE_PRODUCT')"
                      @click.stop.prevent="createDealProductQuickly"
                    />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </teleport>

        <div class="mt-2 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <button
              type="button"
              data-deal-assignee-trigger
              class="inline-flex h-9 min-w-9 px-1 items-center justify-center rounded-md hover:bg-n-alpha-2"
              :title="t('DEALS.CONVERSATION.DEAL_LIST.ASSIGNEES')"
              @click.stop="toggleAssigneePopover(deal, $event)"
            >
              <div
                v-if="getDealAssignees(deal).length"
                class="flex items-center"
              >
                <span
                  v-for="(agent, index) in getDealAssignees(deal).slice(0, 3)"
                  :key="`conv-deal-agent-${deal.id}-${agent.id}`"
                  class="inline-flex"
                  :class="index > 0 ? '-ml-2' : ''"
                >
                  <Avatar
                    :name="agent.name"
                    :src="agent.thumbnail || agent.avatar_url"
                    :size="24"
                    rounded-full
                  />
                </span>
                <span
                  v-if="getDealAssignees(deal).length > 3"
                  class="-ml-2 inline-flex h-6 min-w-6 px-1 items-center justify-center rounded-full bg-n-alpha-2 text-[10px] text-n-slate-12"
                >
                  +{{ getDealAssignees(deal).length - 3 }}
                </span>
              </div>
              <span
                v-else
                class="inline-flex items-center justify-center size-6 rounded-full bg-n-alpha-2 text-n-slate-11"
              >
                <span class="i-lucide-user size-4" />
              </span>
            </button>

            <button
              type="button"
              data-deal-custom-fields-trigger
              class="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
              :title="`${t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.TITLE')}: ${getDealCustomFieldsCount(deal)}`"
              @click.stop="toggleDealCustomFieldsPopover(deal, $event)"
            >
              <span class="i-lucide-code size-6" />
              <span
                class="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-n-brand text-white text-[10px] leading-4 text-center"
              >
                {{ getDealCustomFieldsCount(deal) }}
              </span>
            </button>

            <button
              type="button"
              data-deal-priority-trigger
              class="inline-flex h-9 w-9 items-center justify-center rounded-md text-n-slate-12 hover:bg-n-alpha-2"
              :title="`${t('DEALS.CONVERSATION.DEAL_LIST.PRIORITY')}: ${getDealPriorityMeta(deal).label}`"
              @click.stop="toggleDealPriorityPopover(deal, $event)"
            >
              <span
                class="size-6"
                :class="[getDealPriorityMeta(deal).icon]"
                :style="{ color: getDealPriorityMeta(deal).color }"
              />
            </button>
          </div>

          <span
            class="inline-flex items-center gap-1 text-xs text-n-slate-11"
            :title="`${t('DEALS.CONVERSATION.DEAL_LIST.CREATED_AT')}: ${formatDate(deal.created_at)}`"
          >
            <span class="i-lucide-clock-3 size-4" />
            {{ formatDealCreatedAt(deal.created_at) }}
          </span>
        </div>

        <div
          class="mt-2 border-t border-n-weak/80 pt-2 flex items-start justify-between gap-2"
        >
          <div
            class="min-w-0 flex-1 flex flex-wrap gap-1.5 max-h-12 overflow-hidden"
          >
            <span
              v-for="labelTitle in getVisibleDealLabels(deal)"
              :key="`label-chip-${deal.id}-${labelTitle}`"
              class="inline-flex items-center gap-1.5 rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-12"
            >
              <span
                class="inline-block h-2 w-2 shrink-0 rounded-sm"
                :style="{ backgroundColor: getLabelColorByTitle(labelTitle) }"
              />
              <span class="truncate max-w-[120px]">{{ labelTitle }}</span>
            </span>
            <span
              v-if="!getVisibleDealLabels(deal).length"
              class="text-xs text-n-slate-11 py-1"
            >
              {{ t('DEALS.CONVERSATION.DEAL_LIST.NO_LABELS') }}
            </span>
          </div>

          <button
            type="button"
            data-deal-labels-trigger
            class="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
            :title="`${t('DEALS.CONVERSATION.DEAL_LIST.LABELS')}: ${getDealLabels(deal).length}`"
            @click.stop="toggleDealLabelsPopover(deal, $event)"
          >
            <span class="i-lucide-tags size-5" />
            <span
              v-if="getHiddenDealLabelsCount(deal) > 0"
              class="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-n-brand text-white text-[10px] leading-4 text-center"
            >
              +{{ getHiddenDealLabelsCount(deal) }}
            </span>
          </button>
        </div>

        <teleport to="body">
          <div
            v-if="activeDealPriorityPopoverId === deal.id"
            data-deal-priority-menu
            class="fixed z-[1000] w-[230px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-2 space-y-1"
            :style="dealPriorityPopoverStyle"
          >
            <button
              v-for="option in PRIORITY_OPTIONS"
              :key="`priority-${option.value}`"
              class="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2 text-n-slate-12"
              :disabled="prioritySaving"
              @click="setDealPriority(deal, option.value)"
            >
              <span
                class="size-4"
                :class="[option.icon]"
                :style="{ color: option.color }"
              />
              <span>{{ option.label }}</span>
            </button>
          </div>
        </teleport>

        <teleport to="body">
          <div
            v-if="activeAssigneePopoverDealId === deal.id"
            data-deal-assignee-popover
            class="fixed z-[1000] w-[320px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-3 space-y-3"
            :style="assigneePopoverStyle"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('DEALS.CONVERSATION.DEAL_LIST.ASSIGNEES') }}
            </p>
            <div class="space-y-2">
              <input
                v-model="assigneeSearch"
                type="text"
                class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-solid-1 text-sm"
                :placeholder="
                  t('DEALS.CONVERSATION.DEAL_LIST.SEARCH_AGENT_PLACEHOLDER')
                "
              />
              <div
                class="max-h-52 overflow-y-auto border border-n-weak rounded-md py-1"
              >
                <button
                  v-for="agent in filteredAssigneeOptions"
                  :key="`conv-assignee-option-${deal.id}-${agent.id}`"
                  class="w-full px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2 inline-flex items-center justify-between gap-2"
                  @click="toggleAssigneeSelection(agent)"
                >
                  <span class="inline-flex items-center gap-2 min-w-0">
                    <Avatar
                      :name="agent.name"
                      :src="agent.thumbnail || agent.avatar_url"
                      :size="20"
                      rounded-full
                    />
                    <span class="truncate">{{ agent.name }}</span>
                  </span>
                  <span
                    v-if="isAssigneeSelected(agent.id)"
                    class="i-lucide-check size-4 text-n-brand shrink-0"
                  />
                </button>
                <p
                  v-if="!filteredAssigneeOptions.length"
                  class="px-3 py-2 text-sm text-n-slate-11"
                >
                  {{ t('DEALS.CONVERSATION.DEAL_LIST.NO_AGENT_FOUND') }}
                </p>
              </div>
            </div>
            <div class="flex justify-end gap-2">
              <Button
                slate
                faded
                xs
                :label="t('DEALS.CONVERSATION.PRODUCTS.CANCEL')"
                @click="activeAssigneePopoverDealId = null"
              />
              <Button
                xs
                :is-loading="assigneeSaving"
                :label="t('DEALS.CONVERSATION.PRODUCTS.SAVE')"
                @click="saveDealAssignees(deal)"
              />
            </div>
          </div>
        </teleport>

        <teleport to="body">
          <div
            v-if="activeDealLabelsPopoverId === deal.id"
            data-deal-labels-popover
            class="fixed z-[1000] w-[300px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-3 space-y-3"
            :style="dealLabelsPopoverStyle"
          >
            <input
              v-model="dealLabelsSearch"
              type="text"
              class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
              :placeholder="
                t('DEALS.CONVERSATION.DEAL_LIST.SEARCH_LABEL_PLACEHOLDER')
              "
            />

            <div
              class="max-h-52 overflow-y-auto border border-n-weak rounded-lg divide-y divide-n-weak"
            >
              <button
                v-for="label in filteredLabels"
                :key="`deal-label-${deal.id}-${label.id}`"
                class="w-full px-3 py-2 text-sm hover:bg-n-alpha-2 flex items-center justify-between gap-2 text-n-slate-12"
                :disabled="labelsSaving"
                @click="toggleLabelOnDeal(deal, label.title)"
              >
                <span class="inline-flex items-center gap-2 min-w-0">
                  <span
                    class="inline-flex size-2.5 rounded-full shrink-0"
                    :style="{ backgroundColor: label.color || '#3B82F6' }"
                  />
                  <span class="truncate">{{ label.title }}</span>
                </span>
                <span
                  v-if="getDealLabels(deal).includes(label.title)"
                  class="i-lucide-check size-4 text-green-600 shrink-0"
                />
              </button>
              <p
                v-if="!filteredLabels.length"
                class="px-3 py-2 text-sm text-n-slate-11"
              >
                {{ t('DEALS.CONVERSATION.DEAL_LIST.NO_LABELS_FOUND') }}
              </p>
            </div>

            <button
              class="text-sm text-n-brand hover:underline"
              @click="openAddLabelModal"
            >
              {{ t('DEALS.CONVERSATION.DEAL_LIST.CREATE_LABEL') }}
            </button>
          </div>
        </teleport>

        <teleport to="body">
          <div
            v-if="activeDealCustomFieldsPopoverId === deal.id"
            data-deal-custom-fields-popover
            class="fixed z-[1000] w-[360px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-3 space-y-3"
            :style="dealCustomFieldsPopoverStyle"
          >
            <div class="flex items-center justify-between gap-2">
              <p class="text-sm font-medium text-n-slate-12">
                {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.TITLE') }}
              </p>
              <button
                class="text-xs text-n-slate-11 hover:underline"
                @click="closeDealCustomFieldsPopover"
              >
                {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.CLOSE') }}
              </button>
            </div>

            <input
              v-model="dealCustomFieldsSearch"
              type="text"
              class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-2 text-sm"
              :placeholder="
                t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.SEARCH_PLACEHOLDER')
              "
            />

            <div
              class="max-h-60 overflow-y-auto border border-n-weak rounded-lg divide-y divide-n-weak"
            >
              <div
                v-for="item in getDealCustomFieldsForPopover(deal)"
                :key="`${deal.id}-${item.key}`"
                class="px-3 py-2"
              >
                <p class="text-xs text-n-slate-10">{{ item.label }}</p>
                <p class="text-sm text-n-slate-12 break-words">
                  {{ item.formattedValue }}
                </p>
              </div>
              <p
                v-if="!getDealCustomFieldsForPopover(deal).length"
                class="px-3 py-4 text-sm text-center text-n-slate-11"
              >
                {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.EMPTY_FOUND') }}
              </p>
            </div>

            <div class="flex items-center justify-between">
              <button
                class="text-sm text-n-brand"
                @click="openDealCustomFieldsModal(deal)"
              >
                {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.MANAGE') }}
              </button>
              <button
                class="text-sm text-n-brand"
                @click="openAddDealAttributeModal"
              >
                {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.CREATE') }}
              </button>
            </div>
          </div>
        </teleport>
      </div>
    </div>

    <Button
      icon="i-lucide-plus"
      xs
      faded
      class="mt-1"
      :label="t('DEALS.CONVERSATION.ADD_NEW_DEAL')"
      @click="openCreateModal"
    />

    <DealModal
      v-if="showCreateModal"
      :show="showCreateModal"
      :deal="selectedDealForForm"
      :pipeline-id="modalPipelineId"
      :stages="modalStages"
      :initial-values="dealModalInitialValues"
      @close="closeCreateModal"
      @submit="submitDealFromModal"
    />

    <woot-modal
      v-if="false"
      v-model:show="showCreateModal"
      :on-close="closeCreateModal"
      size="medium"
    >
      <div class="flex flex-col h-auto overflow-auto overflow-x-hidden">
        <woot-modal-header
          :header-title="
            isEditingMode
              ? t('DEALS.CONVERSATION.DEAL_MODAL.EDIT_TITLE')
              : t('DEALS.CONVERSATION.DEAL_MODAL.CREATE_TITLE')
          "
          :header-content="
            isEditingMode
              ? t('DEALS.CONVERSATION.DEAL_MODAL.EDIT_DESC')
              : t('DEALS.CONVERSATION.DEAL_MODAL.CREATE_DESC')
          "
        />
        <div class="modal-content w-full !pt-8 space-y-4">
          <template v-if="!createDealBlockedState">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div class="space-y-3">
                <div class="space-y-2">
                  <label class="text-sm font-medium text-n-slate-12 block">{{
                    t('DEALS.CONVERSATION.DEAL_LIST.FORM_TITLE')
                  }}</label>
                  <input
                    v-model="title"
                    type="text"
                    class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-solid-1 text-sm"
                    :placeholder="
                      t('DEALS.CONVERSATION.DEAL_MODAL.TITLE_PLACEHOLDER')
                    "
                  />
                </div>

                <div class="space-y-2">
                  <label class="text-sm font-medium text-n-slate-12 block">{{
                    t('DEALS.CONVERSATION.DEAL_LIST.FORM_DESCRIPTION')
                  }}</label>
                  <Editor
                    v-model="description"
                    editor-key="deal-description-conversation"
                    :placeholder="
                      t('DEALS.CONVERSATION.DEAL_LIST.DESCRIPTION_PLACEHOLDER')
                    "
                    :max-length="5000"
                    :show-character-count="false"
                  />
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                  <Button
                    v-if="isEditingMode"
                    slate
                    faded
                    class="!justify-center"
                    :label="t('DEALS.CONVERSATION.DEAL_LIST.DELETE')"
                    @click="deleteEditingDeal"
                  />
                  <div class="space-y-1 md:col-span-1">
                    <label class="text-xs font-medium text-n-slate-11 block">{{
                      t('DEALS.CONVERSATION.DEAL_LIST.STAGE')
                    }}</label>
                    <ComboBox
                      v-model="selectedStageId"
                      :options="stageSelectOptions"
                      :placeholder="
                        t('DEALS.CONVERSATION.DEAL_MODAL.STAGE_PLACEHOLDER')
                      "
                      class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
                    />
                  </div>
                  <div class="space-y-1 md:col-span-1">
                    <label class="text-xs font-medium text-n-slate-11 block">{{
                      t('DEALS.CONVERSATION.DEAL_LIST.LABELS')
                    }}</label>
                    <TagMultiSelectComboBox
                      v-model="selectedLabelTitles"
                      :options="labelSelectOptions"
                      :placeholder="
                        t('DEALS.CONVERSATION.DEAL_LIST.SELECT_LABELS')
                      "
                      :search-placeholder="
                        t(
                          'DEALS.CONVERSATION.DEAL_LIST.SEARCH_LABEL_PLACEHOLDER'
                        )
                      "
                      :empty-state="t('DEALS.CONVERSATION.DEAL_LIST.NO_LABELS')"
                      use-fixed-dropdown
                    />
                  </div>
                </div>
              </div>

              <div class="space-y-3">
                <div class="space-y-2">
                  <label class="text-sm font-medium text-n-slate-12 block">{{
                    t('DEALS.CONVERSATION.DEAL_MODAL.PIPELINE_LABEL')
                  }}</label>
                  <ComboBox
                    v-model="selectedPipelineId"
                    :options="pipelineSelectOptions"
                    :placeholder="
                      t('DEALS.CONVERSATION.DEAL_MODAL.PIPELINE_PLACEHOLDER')
                    "
                    class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
                  />
                </div>

                <div class="space-y-2">
                  <label class="text-sm font-medium text-n-slate-12 block">{{
                    t('DEALS.CONVERSATION.DEAL_LIST.AGENTS')
                  }}</label>
                  <TagMultiSelectComboBox
                    v-model="selectedAgentIds"
                    :options="assigneeSelectOptions"
                    :placeholder="
                      t('DEALS.CONVERSATION.DEAL_LIST.SELECT_AGENTS')
                    "
                    :search-placeholder="
                      t('DEALS.CONVERSATION.DEAL_LIST.SEARCH_AGENT_PLACEHOLDER')
                    "
                    :empty-state="t('DEALS.CONVERSATION.DEAL_LIST.NO_AGENTS')"
                    use-fixed-dropdown
                  />
                </div>

                <div class="space-y-2">
                  <label class="text-sm font-medium text-n-slate-12 block">{{
                    t('DEALS.CONVERSATION.DEAL_LIST.CONTACT')
                  }}</label>
                  <ComboBox
                    v-model="selectedContactId"
                    :options="contactSelectOptions"
                    :placeholder="
                      t('DEALS.CONVERSATION.DEAL_LIST.SELECT_CONTACT')
                    "
                    :search-placeholder="
                      t('DEALS.CONVERSATION.DEAL_LIST.SEARCH_CONTACT')
                    "
                    :empty-state="t('DEALS.CONVERSATION.DEAL_LIST.NO_CONTACTS')"
                    class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
                  />
                </div>

                <div class="space-y-2">
                  <label class="text-sm font-medium text-n-slate-12 block">{{
                    t('DEALS.CONVERSATION.DEAL_LIST.CONVERSATION')
                  }}</label>
                  <ComboBox
                    v-model="selectedConversationId"
                    :options="conversationSelectOptions"
                    :placeholder="
                      t('DEALS.CONVERSATION.DEAL_LIST.SELECT_CONVERSATION')
                    "
                    :search-placeholder="
                      t('DEALS.CONVERSATION.DEAL_LIST.SEARCH_CONVERSATION')
                    "
                    :empty-state="
                      t('DEALS.CONVERSATION.DEAL_LIST.NO_CONVERSATIONS')
                    "
                    class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
                  />
                </div>

                <div class="grid grid-cols-2 gap-2">
                  <div class="space-y-1">
                    <label class="text-xs font-medium text-n-slate-11 block">{{
                      t('DEALS.CONVERSATION.DEAL_LIST.START_DATE')
                    }}</label>
                    <input
                      v-model="startDate"
                      type="date"
                      class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-solid-1 text-sm"
                    />
                  </div>
                  <div class="space-y-1">
                    <label class="text-xs font-medium text-n-slate-11 block">{{
                      t('DEALS.CONVERSATION.DEAL_MODAL.CLOSE_DATE_LABEL')
                    }}</label>
                    <input
                      v-model="closeDate"
                      type="date"
                      class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-solid-1 text-sm"
                    />
                  </div>
                </div>

                <div class="space-y-1">
                  <label class="text-xs font-medium text-n-slate-11 block">{{
                    t('DEALS.CONVERSATION.DEAL_LIST.LABELS')
                  }}</label>
                  <TagMultiSelectComboBox
                    v-model="selectedLabelTitles"
                    :options="labelSelectOptions"
                    :placeholder="
                      t('DEALS.CONVERSATION.DEAL_LIST.SELECT_LABELS')
                    "
                    :search-placeholder="
                      t('DEALS.CONVERSATION.DEAL_LIST.SEARCH_LABEL_PLACEHOLDER')
                    "
                    :empty-state="t('DEALS.CONVERSATION.DEAL_LIST.NO_LABELS')"
                    use-fixed-dropdown
                  />
                </div>

                <div class="relative">
                  <button
                    data-create-products-trigger
                    class="w-full rounded-lg bg-n-brand/10 text-n-brand text-sm font-medium py-2.5 border border-n-brand/20 hover:bg-n-brand/15 transition"
                    @click="showProductsPopover = !showProductsPopover"
                  >
                    {{ t('DEALS.CONVERSATION.DEAL_MODAL.CONFIGURE_PRODUCTS') }}
                  </button>

                  <div
                    v-if="showProductsPopover"
                    data-create-products-popover
                    class="mt-2 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg p-3 space-y-3"
                  >
                    <template v-if="productsView === 'summary'">
                      <div
                        v-if="!selectedProducts.length"
                        class="text-center py-6 text-sm text-n-slate-11"
                      >
                        {{ t('DEALS.CONVERSATION.PRODUCTS.EMPTY') }}
                      </div>

                      <div v-else class="space-y-2 max-h-52 overflow-y-auto">
                        <div
                          v-for="item in selectedProducts"
                          :key="item.product_id"
                          class="border-b border-n-weak pb-2 last:border-0 last:pb-0"
                        >
                          <div class="flex items-center justify-between gap-2">
                            <p class="text-sm text-n-slate-12 truncate">
                              {{ item.name }}
                            </p>
                            <span class="text-sm font-medium">{{
                              formatCurrency(item.unit_price)
                            }}</span>
                          </div>
                          <div
                            class="flex items-center justify-between gap-2 mt-1"
                          >
                            <p class="text-xs text-n-slate-10 truncate">
                              {{ item.sku || '-' }}
                            </p>
                            <div
                              class="flex items-center border border-n-weak rounded"
                            >
                              <button
                                class="px-2 py-0.5 text-n-slate-11"
                                @click="decreaseQty(item)"
                              >
                                -
                              </button>
                              <span class="px-2 text-sm">{{
                                item.quantity
                              }}</span>
                              <button
                                class="px-2 py-0.5 text-n-slate-11"
                                @click="increaseQty(item)"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="text-right">
                        <button
                          class="text-sm text-n-brand"
                          @click="productsView = 'catalog'"
                        >
                          {{ t('DEALS.CONVERSATION.PRODUCTS.ADD_PRODUCT') }}
                        </button>
                      </div>

                      <div
                        class="border-t border-n-weak pt-2 space-y-1.5 text-sm"
                      >
                        <div class="flex justify-between">
                          <span>{{
                            t('DEALS.CONVERSATION.PRODUCTS.TOTAL_PRODUCTS')
                          }}</span>
                          <span>{{ formatCurrency(subtotalProducts) }}</span>
                        </div>
                        <div class="flex items-center justify-between gap-2">
                          <span>{{
                            t('DEALS.CONVERSATION.PRODUCTS.COUPON')
                          }}</span>
                          <input
                            v-model="couponCode"
                            type="text"
                            class="w-40 px-2 py-1 border rounded border-n-weak text-xs text-right"
                            :placeholder="
                              t(
                                'DEALS.CONVERSATION.PRODUCTS.COUPON_PLACEHOLDER'
                              )
                            "
                            @blur="applyCoupon"
                          />
                        </div>
                        <div class="flex items-center justify-between gap-2">
                          <span>{{
                            t('DEALS.CONVERSATION.PRODUCTS.DISCOUNT')
                          }}</span>
                          <input
                            v-model="manualDiscount"
                            type="number"
                            min="0"
                            step="0.01"
                            class="w-28 px-2 py-1 border rounded border-n-weak text-xs text-right"
                          />
                        </div>
                        <div class="flex items-center justify-between gap-2">
                          <span>{{
                            t('DEALS.CONVERSATION.PRODUCTS.SURCHARGE')
                          }}</span>
                          <input
                            v-model="surcharge"
                            type="number"
                            min="0"
                            step="0.01"
                            class="w-28 px-2 py-1 border rounded border-n-weak text-xs text-right"
                          />
                        </div>
                        <div class="flex items-center justify-between gap-2">
                          <span>{{
                            t('DEALS.CONVERSATION.PRODUCTS.SHIPPING')
                          }}</span>
                          <input
                            v-model="shipping"
                            type="number"
                            min="0"
                            step="0.01"
                            class="w-28 px-2 py-1 border rounded border-n-weak text-xs text-right"
                          />
                        </div>
                      </div>

                      <div
                        class="border-t border-n-weak pt-2 flex items-center justify-between text-base font-semibold"
                      >
                        <span>{{
                          t('DEALS.CONVERSATION.PRODUCTS.TOTAL')
                        }}</span>
                        <span>{{ formatCurrency(finalTotal) }}</span>
                      </div>
                    </template>

                    <template v-else>
                      <input
                        v-model="productSearch"
                        type="text"
                        class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-solid-1 text-sm"
                        :placeholder="
                          t('DEALS.CONVERSATION.PRODUCTS.SEARCH_PLACEHOLDER')
                        "
                      />

                      <div
                        class="max-h-56 overflow-y-auto border border-n-weak rounded-lg py-1"
                      >
                        <button
                          v-for="product in filteredCatalogProducts"
                          :key="product.id"
                          class="w-full text-left px-3 py-1.5 text-sm hover:bg-n-alpha-2"
                          @click="addProduct(product)"
                        >
                          {{ product.name }}
                        </button>
                        <p
                          v-if="!filteredCatalogProducts.length"
                          class="px-3 py-2 text-sm text-n-slate-11"
                        >
                          {{ t('DEALS.CONVERSATION.PRODUCTS.EMPTY_FOUND') }}
                        </p>
                      </div>

                      <div class="flex items-center justify-between">
                        <button
                          class="text-sm text-n-slate-11"
                          @click="productsView = 'summary'"
                        >
                          {{ t('DEALS.CONVERSATION.PRODUCTS.BACK') }}
                        </button>
                        <button
                          class="text-sm text-n-brand"
                          @click="
                            showCreateProductForm = !showCreateProductForm
                          "
                        >
                          {{ t('DEALS.CONVERSATION.PRODUCTS.CREATE') }}
                        </button>
                      </div>

                      <div
                        v-if="showCreateProductForm"
                        class="space-y-2 border border-n-weak rounded-lg p-2"
                      >
                        <input
                          v-model="newProduct.name"
                          type="text"
                          class="w-full px-2 py-1.5 border rounded border-n-weak text-sm"
                          :placeholder="
                            t(
                              'DEALS.CONVERSATION.PRODUCTS.NEW_PRODUCT_NAME_PLACEHOLDER'
                            )
                          "
                        />
                        <input
                          v-model="newProduct.sku"
                          type="text"
                          class="w-full px-2 py-1.5 border rounded border-n-weak text-sm"
                          :placeholder="
                            t(
                              'DEALS.CONVERSATION.PRODUCTS.NEW_PRODUCT_SKU_PLACEHOLDER'
                            )
                          "
                        />
                        <input
                          v-model="newProduct.price"
                          type="number"
                          min="0"
                          step="0.01"
                          class="w-full px-2 py-1.5 border rounded border-n-weak text-sm"
                          :placeholder="
                            t(
                              'DEALS.CONVERSATION.PRODUCTS.NEW_PRODUCT_PRICE_PLACEHOLDER'
                            )
                          "
                        />
                        <div class="flex justify-end gap-2">
                          <Button
                            slate
                            faded
                            xs
                            :label="t('DEALS.CONVERSATION.PRODUCTS.CANCEL')"
                            @click="showCreateProductForm = false"
                          />
                          <Button
                            xs
                            :label="
                              t('DEALS.CONVERSATION.PRODUCTS.SAVE_PRODUCT')
                            "
                            @click="createProductQuickly"
                          />
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div
            v-else
            class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 space-y-2"
          >
            <div class="inline-flex items-center gap-2 text-amber-11">
              <span class="i-lucide-shield-alert size-4" />
              <span class="text-sm font-medium">{{
                createDealBlockedTitle
              }}</span>
            </div>
            <p class="text-sm text-n-slate-11">
              {{ createDealBlockedDescription }}
            </p>
          </div>

          <div class="flex justify-end gap-2 pt-2">
            <Button
              slate
              faded
              :label="t('DEALS.CONVERSATION.PRODUCTS.CANCEL')"
              @click="closeCreateModal"
            />
            <Button
              v-if="!createDealBlockedState"
              :is-loading="creating"
              :label="
                isEditingMode
                  ? t('DEALS.CONVERSATION.DEAL_MODAL.SAVE_CHANGES')
                  : t('DEALS.CONVERSATION.DEAL_MODAL.CREATE_BUTTON')
              "
              @click="createDeal"
            />
          </div>
        </div>
      </div>
    </woot-modal>

    <woot-modal v-model:show="showLoseDealModal" :on-close="closeLoseDealModal">
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header
          :header-title="t('DEALS.CONVERSATION.LOSS_MODAL.TITLE')"
        />
        <div class="modal-content w-full !pt-8 space-y-4">
          <p class="text-sm text-n-slate-11">
            {{ t('DEALS.CONVERSATION.LOSS_MODAL.DESC') }}
          </p>

          <label class="text-sm font-medium text-n-slate-12 block">{{
            t('DEALS.CONVERSATION.LOSS_MODAL.REASON_LABEL')
          }}</label>
          <ComboBox
            v-model="selectedLossReasonId"
            :options="
              lossReasons.map(item => ({ label: item.name, value: item.id }))
            "
            :placeholder="t('DEALS.CONVERSATION.LOSS_MODAL.REASON_PLACEHOLDER')"
            class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
          />

          <label class="text-sm font-medium text-n-slate-12 block">
            {{ t('DEALS.CONVERSATION.LOSS_MODAL.JUSTIFICATION_LABEL') }}
            <span
              v-if="selectedLossReason?.requires_justification"
              class="text-ruby-10"
              >*</span
            >
          </label>
          <textarea
            v-model="lossJustification"
            rows="4"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-solid-1 text-sm resize-none"
            :placeholder="
              selectedLossReason?.requires_justification
                ? t('DEALS.CONVERSATION.LOSS_MODAL.JUSTIFICATION_REQUIRED')
                : t('DEALS.CONVERSATION.LOSS_MODAL.JUSTIFICATION_OPTIONAL')
            "
          />

          <div class="flex justify-end gap-2 pt-2">
            <Button
              slate
              faded
              :label="t('DEALS.CONVERSATION.LOSS_MODAL.CANCEL')"
              @click="closeLoseDealModal"
            />
            <Button
              :is-loading="loseDealLoading"
              :label="t('DEALS.CONVERSATION.LOSS_MODAL.CONFIRM')"
              @click="confirmLoseDeal"
            />
          </div>
        </div>
      </div>
    </woot-modal>

    <woot-modal
      v-model:show="showDealCustomFieldsModal"
      :on-close="closeDealCustomFieldsModal"
    >
      <div class="flex min-h-0 max-h-[82vh] flex-col">
        <woot-modal-header
          :header-title="t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.MODAL_TITLE')"
          :header-content="t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.MODAL_DESC')"
        />
        <div class="modal-content w-full !pt-8 flex min-h-0 flex-col gap-4">
          <div class="space-y-2">
            <label class="text-sm font-medium text-n-slate-12 block">
              {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.FIELD_LABEL') }}
            </label>
            <ComboBox
              v-model="selectedCustomAttributeKey"
              :options="dealAttributeDefinitionOptions"
              :placeholder="
                t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.FIELD_PLACEHOLDER')
              "
              class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
            />
            <div class="flex justify-end">
              <Button
                xs
                :label="t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.ADD_BUTTON')"
                @click="addCustomFieldToDraft"
              />
            </div>
          </div>

          <div class="flex-1 min-h-0 space-y-3 overflow-y-auto pr-1">
            <div
              v-for="key in dealCustomFieldKeys"
              :key="key"
              class="rounded-lg border border-n-weak p-3 space-y-2.5"
            >
              <div class="flex items-center justify-between gap-2">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ getDraftCustomFieldLabel(key) }}
                </label>
                <button
                  class="text-xs text-ruby-10 hover:underline"
                  @click="removeCustomFieldFromDraft(key)"
                >
                  {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.REMOVE') }}
                </button>
              </div>

              <input
                v-if="
                  ['text', 'link', 'phone_number'].includes(
                    getDraftCustomFieldType(key)
                  )
                "
                :value="dealCustomFieldsDraft[key] || ''"
                type="text"
                class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
                @input="updateDraftCustomFieldValue(key, $event.target.value)"
              />

              <input
                v-else-if="
                  ['number', 'currency'].includes(getDraftCustomFieldType(key))
                "
                :value="dealCustomFieldsDraft[key] || ''"
                type="number"
                class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
                @input="
                  updateDraftCustomFieldValue(
                    key,
                    $event.target.value === ''
                      ? ''
                      : Number($event.target.value)
                  )
                "
              />

              <input
                v-else-if="getDraftCustomFieldType(key) === 'date'"
                :value="dealCustomFieldsDraft[key] || ''"
                type="date"
                class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
                @input="updateDraftCustomFieldValue(key, $event.target.value)"
              />

              <button
                v-else-if="getDraftCustomFieldType(key) === 'list'"
                data-custom-field-list-trigger
                class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-solid-1 text-sm text-left text-n-slate-12 inline-flex items-center justify-between gap-2 hover:border-n-slate-6"
                @click="toggleCustomFieldListPopover(key, $event)"
              >
                <span
                  class="truncate"
                  :class="{
                    'text-n-slate-11': !(dealCustomFieldsDraft[key] || ''),
                  }"
                >
                  {{ getDraftCustomFieldSelectedLabel(key) }}
                </span>
                <span
                  class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-11"
                />
              </button>

              <teleport to="body">
                <div
                  v-if="activeCustomFieldListKey === key"
                  data-custom-field-list-popover
                  class="fixed z-[1200] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-n-strong bg-n-solid-1 shadow-lg"
                  :style="{
                    ...customFieldListPopoverStyle,
                    width: `${customFieldListPopoverWidth}px`,
                  }"
                >
                  <div class="relative border-b border-n-strong">
                    <span
                      class="absolute i-lucide-search top-2.5 size-4 left-3 text-n-slate-11"
                    />
                    <input
                      v-model="customFieldListSearch"
                      type="text"
                      class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
                      :placeholder="
                        t(
                          'DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.SEARCH_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                  <div class="max-h-60 overflow-y-auto py-1">
                    <button
                      v-for="option in getFilteredDraftCustomFieldOptions(key)"
                      :key="`opt-${key}-${option}`"
                      class="w-full px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2 inline-flex items-center justify-between gap-2"
                      :class="{
                        'bg-n-alpha-2 font-medium':
                          dealCustomFieldsDraft[key] === option,
                      }"
                      @click="selectCustomFieldListOption(key, option)"
                    >
                      <span class="truncate">{{ option }}</span>
                      <span
                        v-if="dealCustomFieldsDraft[key] === option"
                        class="i-lucide-check size-4 text-n-slate-11 shrink-0"
                      />
                    </button>
                    <p
                      v-if="!getFilteredDraftCustomFieldOptions(key).length"
                      class="px-3 py-2 text-sm text-n-slate-11"
                    >
                      {{
                        t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.EMPTY_FOUND')
                      }}
                    </p>
                  </div>
                </div>
              </teleport>

              <label
                v-if="getDraftCustomFieldType(key) === 'checkbox'"
                class="inline-flex items-center gap-2 text-sm text-n-slate-12"
              >
                <input
                  :checked="Boolean(dealCustomFieldsDraft[key])"
                  type="checkbox"
                  @change="
                    updateDraftCustomFieldValue(key, $event.target.checked)
                  "
                />
                {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.ACTIVE') }}</label
              >

              <input
                v-if="
                  ![
                    'text',
                    'link',
                    'phone_number',
                    'number',
                    'currency',
                    'date',
                    'list',
                    'checkbox',
                  ].includes(getDraftCustomFieldType(key))
                "
                :value="dealCustomFieldsDraft[key] || ''"
                type="text"
                class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
                @input="updateDraftCustomFieldValue(key, $event.target.value)"
              />
            </div>

            <p
              v-if="!dealCustomFieldKeys.length"
              class="text-sm text-center text-n-slate-11 py-6"
            >
              {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.EMPTY') }}
            </p>
          </div>

          <div class="shrink-0 flex items-center justify-between pt-1">
            <button
              class="text-sm text-n-brand"
              @click="openAddDealAttributeModal"
            >
              {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.CREATE_NEW') }}
            </button>
            <div class="flex gap-2">
              <Button
                slate
                faded
                :label="t('DEALS.CONVERSATION.PRODUCTS.CANCEL')"
                @click="closeDealCustomFieldsModal"
              />
              <Button
                :is-loading="customFieldsSaving"
                :label="t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.SAVE')"
                @click="saveDealCustomFields"
              />
            </div>
          </div>
        </div>
      </div>
    </woot-modal>

    <AddAttribute
      v-if="showAddDealAttributeModal"
      :on-close="closeAddDealAttributeModal"
      :selected-attribute-model-tab="2"
      hide-attribute-model-selector
    />

    <woot-modal v-model:show="showAddLabelModal" :on-close="closeAddLabelModal">
      <AddLabel @close="closeAddLabelModal" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeleteDealPopup"
      :on-confirm="confirmDeleteDeal"
      :title="t('DEALS.CONVERSATION.DELETE_MODAL.TITLE')"
      :message="t('DEALS.CONVERSATION.DELETE_MODAL.MESSAGE')"
      :message-value="deleteMessageValue"
      :confirm-text="t('DEALS.CONVERSATION.DELETE_MODAL.CONFIRM')"
      :reject-text="t('DEALS.CONVERSATION.DELETE_MODAL.REJECT')"
      :loading="deleteDealLoading"
    />
  </div>
</template>
